// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chat-layout.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chat-citation-routing.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/isReviewStatusMessage.test.ts
// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chatUtils.test.ts
import {
  type AskPunttMessage,
  type AskPunttProjectData,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowsInSimple,
  ArrowsOutSimple,
  CaretRight,
  CircleNotch,
  ShareNetwork,
  Stop,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type ImperativePanelGroupHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";

import { type ChatArtifact } from "./artifactTypes";
import {
  AskPunttArtifactProvider,
  type AskPunttArtifactContextValue,
} from "./AskPunttArtifactContext";
import {
  buildChatCanvasPath,
  buildChatPath,
  buildChatReviewPath,
} from "./askPunttPaths";
import { chatSearch, type ChatSearch } from "./chatRoute";
import {
  type AskPunttMessageExt,
  collectCanvasMessageIds,
  ASK_PUNTT_SKILL_LABELS,
  clampTwoPaneLayout,
  collectChatUploadAttachments,
  collectReviewTicketIds,
  collectReviewTicketTitles,
  buildThreadArtifactsByAnchorMessageId,
  computeIsAnotherArtifactStreaming,
  connectorsArrayToRecord,
  createActivateCanvasHandler,
  createActivateTicketHandler,
  isCreateAssetProgressSource,
  isHiddenMessage,
  isFirstReviewForChat,
  mergeMessageStatus,
  mergeServerMessagesWithLocal,
  preferAgentSteps,
  preserveUserMessageMentions,
  isFeedMessageFromViewer,
  resolveFeedActorForUserMessage,
  shouldHideRightPaneForFirstReview,
  areAllReviewTicketsComplete,
} from "./chatUtils";
import { ARTIFACT_PRODUCING_TOOLS } from "./components/AgentSteps";
import { ApprovalRequestComposer } from "./components/ApprovalRequestComposer";
import { type ApprovalCardHighlightTarget } from "./components/ApprovalRequestForm";
import { ArtifactPaneCloseButton } from "./components/ArtifactPaneCloseButton";
import { AskPunttDropZone } from "./components/AskPunttDropZone";
import { CampaignReviewPane } from "./components/CampaignReviewPane";
import { type CanvasContent } from "./components/canvas/types";
import { CanvasPane } from "./components/CanvasPane";
import { ChatBreadcrumbTitle } from "./components/ChatBreadcrumbTitle";
import { ChatMessage } from "./components/ChatMessage";
import { ChatThreadArtifactCards } from "./components/ChatThreadArtifactCards";
import { ChatWorkspaceRail } from "./components/ChatWorkspaceRail";
import { CollapsedPaneLabel } from "./components/CollapsedPaneLabel";
import { DayDivider } from "./components/DayDivider";
import { FeedCompactLayoutProvider } from "./components/FeedCompactLayoutContext";
import {
  InputPlusMenu,
  type ConnectorOption,
} from "./components/InputPlusMenu";
import { MentionTextarea } from "./components/MentionTextarea";
import {
  PANE_HEADER_HEIGHT_CLASS,
  PANE_HEADER_ICON_SIZE,
  PANE_HEADER_SIDE_INSET_CLASS,
  PANE_HEADER_SURFACE_CLASS,
  PaneHeaderIconButton,
} from "./components/PaneHeader";
import { PasswordProtectedPdfDialog } from "./components/PasswordProtectedPdfDialog";
import { ProjectLoadingPane } from "./components/ProjectLoadingPane";
import { ProjectStatusPane } from "./components/ProjectStatusPane";
import { PromoteToProjectButton } from "./components/PromoteToProjectButton";
import {
  type QuestionFormHandle,
  QuestionForm,
} from "./components/QuestionForm";
import { ResizeHandle } from "./components/ResizeHandle";
import { ResizeHintTooltip } from "./components/ResizeHintTooltip";
import {
  ReviewArtworkColumn,
  ReviewContextColumn,
  useReviewArtworkContextColumns,
} from "./components/ReviewArtworkContextColumns";
import {
  ReviewChecklist,
  TICKET_REVIEW_CHECKS,
} from "./components/ReviewChecklist";
import {
  ReviewModeControl,
  type ReviewMode,
} from "./components/ReviewModeControl";
import { ReviewNavStrip } from "./components/ReviewNavStrip";
import { ReviewProgressBar } from "./components/ReviewProgressBar";
import { ScrollPauseWhenVisible } from "./components/ScrollPauseWhenVisible";
import { ShareAskPunttChatDialog } from "./components/ShareAskPunttChatDialog";
import { SuggestedResponses } from "./components/SuggestedResponses";
import { ThinkingState } from "./components/ThinkingState";
import { TicketViewPane } from "./components/TicketViewPane";
import { CONNECTOR_ICONS } from "./constants";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { useAutoScrollPauseRegistry } from "./hooks/useAutoScrollPauseRegistry";
import { useChatArtifactWorkspace } from "./hooks/useChatArtifactWorkspace";
import { useChatReviewSummaries } from "./hooks/useChatReviewSummaries";
import { useContainerWidth } from "./hooks/useContainerWidth";
import { useFileDrop } from "./hooks/useFileDrop";
import {
  DEFAULT_STALE_THRESHOLD_MS,
  isKillerFlowAssistantMessage,
  useKillerFlowStream,
} from "./hooks/useKillerFlowStream";
import { usePersistedLayout } from "./hooks/usePersistedLayout";
import { useTicketViewData } from "./hooks/useTicketView";
import { useUiPrefs } from "./hooks/useUiPrefs";
import { askPunttRoute } from "./route";
import {
  isArtifactUnread,
  tailMessageReadReceiptKey,
} from "./utils/askPunttUnread";
import {
  FEED_COMPACT_AUTHOR_MAX_WIDTH_PX,
  feedMessageAuthorKey,
  isFeedAuthorGroupStart,
} from "./utils/messageAuthorGrouping";
import { calendarDayKey, formatDayDivider } from "./utils/messageDateGrouping";
import {
  RESIZE_LAYOUT_MIN_WIDTH_PX,
  REVIEW_CHAT_FALLBACK_PX,
} from "./utils/projectWorkspaceLayout";
import { collectUsedConnectors } from "./utils/usedConnectors";

import {
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
  BREADCRUMB_HEADER_CHIP_CLASS,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import {
  useAskPunttStream,
  type StreamingState,
} from "../../hooks/useAskPunttStream";
import { getGuidelines } from "../../services/brand-guidelines";
import { PasswordProtectedPdfError } from "../../services/upload";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import {
  canUseAskPunttConnectors,
  isMeaningfulGigsOrDemo,
} from "../../utils/auth";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import {
  askPunttKeys,
  invalidateAskPunttUnreadLists,
  useAbortAskPunttMessage,
  useAskPunttChatHistory,
  useAskPunttStreamingState,
  useMarkAskPunttArtifactSeen,
  useMarkAskPunttChatRead,
  useSendAskPunttMessage,
} from "../../utils/queries/askPuntt";
import { setDrawerOpen } from "../../utils/slices/ui";

const STREAM_POLL_MS = 500;
const FULL_POLL_MS = 3000;
const POLL_TIMEOUT_MS = 300_000;
// How long a WebSocket push stays "fresh" enough to be preferred over the RQ
// poll. Past this, a half-open socket's last payload is treated as stale and
// the 500ms poll takes over so the UI never freezes on a dead push.
const WS_FRESHNESS_MS = 5_000;

// Upper bound on how long the "review limbo" predicate keeps polling while
// waiting for the post-review summary after reviews finish. Single-ticket
// reviews can exceed 15m; multi-ticket batches can run longer. These caps
// bound polling when the summary never lands (all batch failed, placeholder
// error, cold-loaded pre-fix chats) without cutting off normal long reviews.
const LIMBO_MAX_MS = 15 * 60 * 1000;
const SUMMARY_LIMBO_MAX_MS = 45 * 60 * 1000;
const MULTI_TICKET_SUMMARY_LIMBO_MAX_MS = 60 * 60 * 1000;

// Per-ticket "review underway" messages: empty content + only thought/text
// agent steps. They look pending but are actually complete server-side events
// emitted by the project review workflow. Exported for regression testing
// (PR #3311 follow-up: stop misclassifying these as streaming on hydration).
export const isReviewStatusMessage = (m: AskPunttMessageExt): boolean =>
  m.role === "assistant" &&
  m.content === "" &&
  (m.agentSteps?.length ?? 0) > 0 &&
  (m.agentSteps ?? []).every(
    (s) =>
      (s as { tool?: string; type?: string }).tool === "thought" ||
      (s as { tool?: string; type?: string }).type === "text",
  );

/** Assistant shell with no visible body yet (pre-stream placeholder). */
export const isEmptyAssistantPlaceholder = (m: AskPunttMessageExt): boolean => {
  if (m.role !== "assistant" || isReviewStatusMessage(m)) return false;
  return (
    !(m.content || m.streamingContent) &&
    !(m.agentSteps?.length ?? 0) &&
    !(m.searchProgress?.length ?? 0) &&
    !(m.contentBlocks?.length ?? 0)
  );
};

// Returns true when the last message is the post-review auto-summary
// placeholder (pending assistant after a review already ran). Used to
// keep the poll alive until the summary resolves, and to keep the
// ReviewChecklist in its completed chrome while that summary streams.
//
// Must NOT match in-flight create_ticket turns. Those are also pending
// assistants with empty content, and treating them as "summary pending"
// flips `reviewChecklistUiComplete` → the floating checklist shows
// "Review complete" while Create Ticket is still running.
//
// Must still match the real summary while it calls `update_chat_details`
// (non-thought tool steps). Dropping those would stop `refetchInterval`
// via `serverHasPendingMessage` and Chat Details would never pick up
// Key Findings until a manual refresh.
/** Index of the last message that proves a review ran (status line or ticket turn). */
export const findLastReviewEvidenceIdx = (
  messages: AskPunttMessageExt[],
  beforeIdx = messages.length,
): number => {
  let lastReviewIdx = -1;
  for (let i = 0; i < beforeIdx; i++) {
    const m = messages[i];
    if (
      isReviewStatusMessage(m) ||
      (m.role === "assistant" && !!m.viewTicketId)
    ) {
      lastReviewIdx = i;
    }
  }
  return lastReviewIdx;
};

/** True when the user sent a new turn after the last review-evidence message. */
export const hasUserMessageAfterReview = (
  messages: AskPunttMessageExt[],
  lastReviewIdx: number,
  beforeIdx = messages.length,
): boolean => {
  if (lastReviewIdx < 0) return false;
  return messages
    .slice(lastReviewIdx + 1, beforeIdx)
    .some((m) => m.role === "user");
};

export const isPostReviewSummaryPending = (
  messages: AskPunttMessageExt[],
): boolean => {
  const last = messages[messages.length - 1];
  if (!last) return false;
  if (last.role !== "assistant") return false;
  if (!(last.status === "pending" || !last.content)) return false;
  if (isReviewStatusMessage(last)) return false;

  // create_ticket (new or follow-up review) is not the post-review summary.
  const hasCreateTicketWork = (last.agentSteps ?? []).some(
    (s) =>
      (s.type === "tool_call" || s.type === "tool_result") &&
      (s as { tool?: string }).tool === "create_ticket",
  );
  if (hasCreateTicketWork) return false;

  const lastReviewIdx = findLastReviewEvidenceIdx(
    messages,
    messages.length - 1,
  );
  if (lastReviewIdx < 0) return false;

  // A user turn after the last review means this pending assistant is a
  // normal follow-up, not the auto post-review summary. Without this gate,
  // follow-ups briefly flip the checklist / toast to "Review complete"
  // (empty pending assistant + prior review evidence) until content lands.
  if (hasUserMessageAfterReview(messages, lastReviewIdx, messages.length - 1)) {
    return false;
  }

  return true;
};

// Assistant turn rembrandt writes after the review pipeline finishes — no
// viewTicketId, not a per-phase review-status message.
export const isPostReviewSummaryMessage = (m: AskPunttMessageExt): boolean =>
  m.role === "assistant" && !m.viewTicketId && !isReviewStatusMessage(m);

// Index of the latest post-review summary message in the chat array, or -1.
// Used to splice the floating ReviewChecklist above the summary tail so
// streaming summary text never renders above the review-status toggle.
// A second review in the same chat must anchor above the newest summary,
// not an earlier cycle's summary message.
export const findPostReviewSummaryMessageIdx = (
  messages: AskPunttMessageExt[],
): number => {
  const lastReviewIdx = findLastReviewEvidenceIdx(messages);
  if (lastReviewIdx < 0) return -1;

  // Only the auto-summary immediately after the review counts — not later
  // assistant follow-ups once the user has sent another message.
  const userAfterReviewIdx = messages.findIndex(
    (m, i) => i > lastReviewIdx && m.role === "user",
  );
  const searchEnd =
    userAfterReviewIdx >= 0 ? userAfterReviewIdx : messages.length;

  for (let i = lastReviewIdx + 1; i < searchEnd; i++) {
    const m = messages[i];
    if (isPostReviewSummaryMessage(m)) {
      return i;
    }
  }
  return -1;
};

/** Pending assistant turn for streaming poll — after the latest user message only. */
export const findPendingAssistantForStreaming = (
  messages: AskPunttMessageExt[],
): AskPunttMessageExt | undefined => {
  let lastUserIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") {
      lastUserIdx = i;
      break;
    }
  }
  if (lastUserIdx < 0) return undefined;
  for (let i = messages.length - 1; i > lastUserIdx; i--) {
    const m = messages[i];
    if (
      m.role === "assistant" &&
      (m.status === "pending" || !m.content) &&
      !isReviewStatusMessage(m)
    ) {
      return m;
    }
  }
  return undefined;
};

/** Message to abort when the user presses Stop.
 *
 * Deliberately NOT findPendingAssistantForStreaming: that one feeds the
 * streaming poll, so it skips review-status messages (they never stream) and
 * gives up when the turn has no assistant message yet. Both exclusions are
 * right there and fatal here — during an artwork review the pending assistant
 * IS the review-status message, so Stop was left with nothing to target,
 * returned early, and never called the backend at all. The workflow (and its
 * model spend) ran on while the composer claimed it had stopped.
 *
 * The id only picks which message gets the "stopped by user" suffix — the
 * cancel itself is chat-scoped (chat.currentWorkflowId), so ANY real message of
 * this turn is enough to reach it. Hence the fall back to the user's own turn.
 */
export const findStopTargetMessageId = (
  messages: AskPunttMessageExt[],
): string | undefined => {
  let lastUserIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") {
      lastUserIdx = i;
      break;
    }
  }
  if (lastUserIdx < 0) return undefined;
  for (let i = messages.length - 1; i > lastUserIdx; i--) {
    if (messages[i].role === "assistant") return messages[i]._id;
  }
  return messages[lastUserIdx]._id;
};

// Decide whether the saved `chat.reviewChecklist` should be treated as the
// final, post-review state (anchored inline at the last review-status
// message) or as in-progress state (rendered as the floating widget below
// the messages).
//
// The naive form — `hasReviewChecklist && !reviewPolling` — misfires during
// the window between the chat workflow saving the initial checklist
// (rembrandt `ask_puntt_workflow.py` step 4b) and the review-trigger child
// workflow setting `chat.reviewStatus` (which is what flips `reviewPolling`
// true). In that window the checklist exists but polling hasn't started,
// so the naive form would call the review "complete" and hide the
// in-progress floating widget that should be showing.
//
// To survive that window we require BOTH (a) we're not currently polling
// AND (b) we have evidence the review actually ran at some point AND
// (c) the review is not still in limbo (viewTicketId stamped but
// reviewStatus transiently null between workflow phases). Evidence
// is either "the component observed a server-side `reviewStatus` at some
// point in its lifetime" (live-session latch) or "the chat already has at
// least one review-status message in its history" (page-reload-after-
// completion). Either is sufficient. The latch must NOT gate on the local
// `reviewPolling` state because that variable is briefly flipped true by
// `setReviewPolling(true)` whenever a new assistant message with a
// `viewTicketId` lands (see `chat.tsx` line ~406) — that flash is an FE
// artifact, not real evidence of the backend review pipeline running.
export const deriveReviewChecklistComplete = (input: {
  hasReviewChecklist: boolean;
  reviewPolling: boolean;
  hasObservedServerReviewStatus: boolean;
  hasReviewStatusMessagesInHistory: boolean;
  reviewInLimbo: boolean;
}): boolean =>
  input.hasReviewChecklist &&
  !input.reviewPolling &&
  !input.reviewInLimbo &&
  (input.hasObservedServerReviewStatus ||
    input.hasReviewStatusMessagesInHistory);

/** True when the review-complete transition toast should fire (not phase gaps). */
export const shouldShowReviewCompleteTransition = (input: {
  wasReviewing: boolean;
  isReviewing: boolean;
  reviewInLimbo: boolean;
}): boolean => input.wasReviewing && !input.isReviewing && !input.reviewInLimbo;

// Whether the composer shows Stop instead of Send.
//
// `waitingForAI` alone is NOT enough. It is session-local state that clears
// the moment a completed assistant message lands (see the response-detection
// effect below), but in the artwork-review flow the chat agent's text is only
// the FIRST few seconds of the turn: rembrandt then runs the review-trigger
// child workflow for minutes. During that window the Temporal workflow is very
// much alive (`AskPunttWorkflow` + `AskPunttReviewTriggerWorkflow` both
// Running) while the composer had already flipped back to Send — the user had
// no way to stop a live review, and a reload made it worse (the initial-load
// latch only sets `waitingForAI` for a user / pending-assistant tail).
//
// So the button also tracks the SERVER's notion of an in-flight review:
// `reviewPolling` (mirrors `chat.reviewStatus`, already stall-guarded) and
// `reviewInLimbo` (ticket stamped, status not yet written).
//
// `stopRequested` is the latch that closes the loop: cancelling the workflow
// does not clear `chat.reviewStatus`, so without it the button would keep
// offering Stop for another ~2 minutes after the user already pressed it
// (until the stall detector fires). It resets once the server actually reports
// no review in flight, so the NEXT review gets its Stop back.
export const shouldShowStopButton = (input: {
  waitingForAI: boolean;
  reviewPolling: boolean;
  reviewInLimbo: boolean;
  stopRequested: boolean;
}): boolean => {
  if (input.waitingForAI) return true;
  if (input.stopRequested) return false;
  return input.reviewPolling || input.reviewInLimbo;
};

// "Limbo" state: a review has been kicked off (the chat agent created a
// ticket — `viewTicketId` is set on the most recent non-review-status
// assistant message) and `chat.reviewStatus` is currently null. The FE
// must keep polling the chat document during this window — otherwise
// React Query stops polling (because nothing else is in-flight), and we
// never see the backend's eventual `reviewStatus` update or the
// post-review summary message, so the UI silently stays stale.
//
// We key on the assistant message's `viewTicketId` (not on
// `chat.reviewChecklist`) because rembrandt's `ask_puntt_workflow.py`
// writes the assistant response FIRST (step 4), then runs
// `generate_initial_checklist_activity` to populate `reviewChecklist`
// (step 4b, ~45s LLM call), then spawns the review-trigger child workflow
// that finally sets `reviewStatus` (step 5). Keying on `viewTicketId` lets
// us survive the entire window from step-4-saved to step-5-reviewStatus,
// not just the narrower step-4b-to-step-5 sub-window.
//
// IMPORTANT: this predicate also covers the **post-review summary**
// window (bug/final-summary). Sequence:
//
//   1. AskPunttReviewTriggerWorkflow runs the review and clears
//      `reviewStatus` in its `finally`.
//   2. `_run_post_review_summary` then re-sets `reviewStatus =
//      "Generating review summary"` and creates a placeholder message.
//   3. The summary agent runs (up to 5 min), saves the message, and
//      `finally` clears `reviewStatus` again.
//
// Between (1) and (2) the FE sees `reviewStatus = null` but the chat
// already has review-status messages in history from the review run.
// Previously this predicate also required
// `!hasReviewStatusMessagesInHistory`, which made it return `false` here
// — combined with the 120 s stall detector flipping `reviewPolling`
// false during step (2)'s long agent run, polling could stop entirely
// and never restart, even though the summary was being saved to
// MongoDB. The user had to refresh the page to see the final summary.
//
// Dropping the historical-evidence clause is safe because
// `hasViewTicketIdOnLastAssistant` automatically flips false the moment
// a real follow-up assistant message lands (the post-review summary
// itself, which has no `viewTicketId`). It skips review-status messages
// — so the summary placeholder, while empty + thought-only, is also
// skipped, and we keep polling. As soon as the summary content is
// flushed (`status: "completed"`, non-empty content), the summary
// becomes the latest non-review-status assistant, the predicate
// returns false, and polling stops.
//
// 15-MINUTE CAP (`LIMBO_MAX_MS`) — legacy cold-load guard only:
//
// Batch multi-ticket reviews call `run_post_review_summary_core` after
// `AskPunttBatchReviewTriggerWorkflow` completes. `isReviewUiInLimbo` keeps
// polling until the summary replaces the viewTicketId turn, with extended
// caps (`SUMMARY_LIMBO_MAX_MS` / `MULTI_TICKET_SUMMARY_LIMBO_MAX_MS`) so
// failure paths do not poll forever.
//
// `isReviewInLimbo` keeps the shorter age cap for cold-loading very old chats
// that still have a viewTicketId assistant as the latest non-status message
// (pre-fix data). Callers pass `null` for `viewTicketAssistantAgeMs` when no
// viewTicketId message exists or its `createdAt` is missing — that
// case must return `false` (no limbo).
//
// Exported as a pure helper so the polling decision can be tested without
// instantiating React Query.
export const isReviewInLimbo = (input: {
  hasViewTicketIdOnLastAssistant: boolean;
  serverReviewStatus: string | null | undefined;
  viewTicketAssistantAgeMs: number | null;
}): boolean => {
  if (!input.hasViewTicketIdOnLastAssistant) return false;
  if (input.serverReviewStatus) return false;
  if (input.viewTicketAssistantAgeMs === null) return false;
  return input.viewTicketAssistantAgeMs <= LIMBO_MAX_MS;
};

// UI + polling limbo for suppressing false "Review complete" and keeping
// chat history refetch alive until the post-review summary lands.
//
// Stay in limbo until the post-review summary replaces the viewTicketId turn
// (`hasViewTicketIdOnLastAssistant` flips false), bounded by age caps when
// the summary never lands.
export const isReviewUiInLimbo = (input: {
  hasViewTicketIdOnLastAssistant: boolean;
  serverReviewStatus: string | null | undefined;
  viewTicketAssistantAgeMs: number | null;
  isMultiTicketReview: boolean;
}): boolean => {
  if (!input.hasViewTicketIdOnLastAssistant) return false;
  if (input.serverReviewStatus) return false;
  if (input.viewTicketAssistantAgeMs === null) return true;
  const cap = input.isMultiTicketReview
    ? MULTI_TICKET_SUMMARY_LIMBO_MAX_MS
    : SUMMARY_LIMBO_MAX_MS;
  return input.viewTicketAssistantAgeMs <= cap;
};

/** True when chat.projectData is a real project plan (stages), not a findings-only stub. */
export const hasProjectPlanStages = (
  projectData: AskPunttProjectData | null | undefined,
): boolean =>
  Array.isArray(projectData?.stages) && (projectData?.stages?.length ?? 0) > 0;

// Shared scan for the latest non-review-status assistant message,
// iterating from the end and skipping per-phase review-status messages
// (empty content + thought-only `agentSteps`) which never carry
// `viewTicketId` and would mask the real signal during the review
// pipeline's mid-flight status churn.
const findLatestNonReviewStatusAssistant = (
  messages: AskPunttMessageExt[],
): AskPunttMessageExt | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== "assistant") continue;
    if (isReviewStatusMessage(m)) continue;
    return m;
  }
  return null;
};

// True when the most recent non-review-status assistant message carries a
// `viewTicketId` — the freshest FE-visible signal that the chat agent
// has just kicked off a review on this turn.
//
// Exported so the polling decision can be tested without instantiating
// React Query.
export const hasViewTicketIdOnLastAssistant = (
  messages: AskPunttMessageExt[],
): boolean => {
  const m = findLatestNonReviewStatusAssistant(messages);
  return !!m?.viewTicketId;
};

// Returns the `createdAt` of the latest non-review-status assistant
// message that carries a `viewTicketId`, or `null` if no such message
// exists. Drives the 15-min `isReviewInLimbo` cap so the predicate can
// stop polling once a multi-ticket batch flow has been idle long
// enough that any post-review summary would already have landed (see
// the cap section of the `isReviewInLimbo` doc above).
//
// Exported for unit testing.
export const getLatestViewTicketAssistantCreatedAt = (
  messages: AskPunttMessageExt[],
): string | null => {
  const m = findLatestNonReviewStatusAssistant(messages);
  if (!m || !m.viewTicketId) return null;
  // Defensive: schema requires `createdAt`, but narrow at runtime in
  // case a malformed payload reaches us — we'd rather return null and
  // skip the time-cap check than throw inside `refetchInterval`.
  return typeof m.createdAt === "string" ? m.createdAt : null;
};

// Whether the single floating ReviewChecklist should render. Pulled out as a
// pure helper so the truth table can be pinned with unit tests without
// instantiating the whole chat component.
//
// The `reviewChecklistAnchorIdx === -1` fallback handles single-ticket
// "general" workflow chats: rembrandt's project review workflow emits
// empty-content "review underway" messages that the inline anchor at the
// last review-status message attaches to, but the single-ticket flow never
// produces those messages. So when `deriveReviewChecklistComplete` flips
// true (e.g. the `hasObservedServerReviewStatusRef` latch fired during a
// mid-flight phase like "Preparing artwork for AI review" and then
// `reviewStatus` dropped back to null between phases or after a failed
// review-trigger child workflow), `reviewChecklistAnchorIdx` stays at -1
// and the inline render never fires. Without this fallback the floating
// widget would also drop out and the user would lose the checklist UI
// entirely even though `chat.reviewChecklist` is populated in MongoDB.
// Keep the floating widget alive so the saved checklist stays visible
// regardless of whether the latch reflects a real completion or a
// mid-flight false positive.
export const shouldShowFloatingReviewChecklist = (input: {
  waitingForAI: boolean;
  lastIsAssistant: boolean;
  /**
   * True when the last message is a still-incomplete assistant message —
   * i.e. `status === "pending"` or content is empty because the agent
   * tool loop hasn't finished writing it yet.
   *
   * Load-bearing for the in-flight review window: in the killer-flow path
   * (any chat where the user asks for a "review"), rembrandt writes a
   * pending assistant placeholder during step 4 (~10-30s for the agent
   * to call `create_ticket`), then continues running for another
   * 60-240s while step 4b populates `chat.reviewChecklist` and step 5
   * spawns the review-trigger child workflow. Throughout that window
   * `waitingForAI` stays true (FE only clears it when a non-pending
   * assistant message lands — see chat.tsx ~538) AND `lastIsAssistant`
   * is true (the placeholder exists). Before this flag was added, the
   * `showWhileWaiting` branch required `!lastIsAssistant`, so the
   * floating widget never rendered during this critical window even
   * though the BE had already signalled review activity (create_ticket
   * fired, then reviewChecklist landed). User-visible symptom: KJ
   * uploaded a file, asked for a review, and saw nothing for 2-4
   * minutes — the test for this case
   * (ask.puntt.review-checklist.spec.ts) hung waiting for the widget.
   */
  lastAssistantIsPending: boolean;
  reviewPolling: boolean;
  hasReviewChecklist: boolean;
  isKillerFlowStreaming: boolean;
  hasCreateTicket: boolean;
  reviewChecklistComplete: boolean;
  reviewChecklistAnchorIdx: number;
  /**
   * True when the backend is driving the live v2 stage bar (`chat.reviewStages`).
   * Only the artwork-review-v2 engine writes that field, so its presence means a
   * real, phase-accurate progress surface is already on screen — the legacy
   * client-side-timer checklist would render alongside it and contradict it.
   *
   * The v2 engine clears `reviewChecklist` when it seeds the bar, so this guard
   * is belt-and-braces: it also covers chats that already carry a checklist from
   * an earlier v1 review, where `hasReviewChecklist` alone is enough to show the
   * widget (see both branches below).
   */
  hasReviewStages: boolean;
  /** User sent a new message after the review finished — hide legacy checklist. */
  hasUserFollowUpAfterReview?: boolean;
}): boolean => {
  if (input.hasReviewStages) return false;

  if (input.hasUserFollowUpAfterReview && !input.reviewPolling) {
    return false;
  }

  const checklistHasNoAnchor = input.reviewChecklistAnchorIdx === -1;
  const completeWithAnchor =
    input.reviewChecklistComplete && !checklistHasNoAnchor;

  // Show after a COMPLETED assistant response when a review is in flight
  // or the saved checklist is already on the chat doc.
  const showAfterResponse =
    !input.waitingForAI &&
    input.lastIsAssistant &&
    (input.reviewPolling ||
      input.hasReviewChecklist ||
      input.isKillerFlowStreaming) &&
    !completeWithAnchor;

  // "While waiting" covers:
  //   (a) the user just sent the message, no assistant message exists
  //       yet (last role === "user"), OR
  //   (b) the agent has written a pending placeholder assistant message
  //       but is still mid-loop (last role === "assistant" with empty/
  //       pending content). This is the dominant in-flight state of a
  //       real review and must NOT block the widget.
  const lastIsUserOrPendingAssistant =
    !input.lastIsAssistant || input.lastAssistantIsPending;

  const showWhileWaiting =
    input.waitingForAI &&
    lastIsUserOrPendingAssistant &&
    (input.reviewPolling ||
      input.hasReviewChecklist ||
      input.hasCreateTicket) &&
    !completeWithAnchor;

  return showAfterResponse || showWhileWaiting;
};

export default function AskPunttChat({
  chatId: chatIdProp,
  projectId: projectIdProp,
  onProjectMutated,
  timelineExtras = [],
  messagePresentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedMembers,
  suppressRightPane = false,
  onProjectReviewOpen,
  onProjectCanvasOpen,
  onArtifactsChange,
  onApprovalCardHighlight,
}: {
  /**
   * When provided, drives the chat from this id instead of the route param.
   * Lets the Ask Puntt Projects workspace embed the full streaming chat
   * renderer in its center column for a project's shared / personal chats.
   * Omitted → behaves exactly as the standalone `ask-puntt/$chatId` route.
   */
  chatId?: string;
  /**
   * Project id for shared-feed composers (approval request cards). When omitted,
   * falls back to `chat.projectId` from history.
   */
  projectId?: string | null;
  /**
   * Hover/focus on an in-chat approval card — campaign highlights the matching
   * Reviews sidebar row.
   */
  onApprovalCardHighlight?: (
    target: ApprovalCardHighlightTarget | null,
  ) => void;
  /**
   * Fired when a completed assistant message reports it mutated the project
   * (`projectMutated`). The campaign workspace uses this to refetch the project
   * so the docs panel + header reflect the agent's change immediately.
   */
  onProjectMutated?: () => void;
  /**
   * Project-only feed events to interleave into the message timeline by
   * `createdAt` (imports, suggestions, memory, version diffs, reviews,
   * lifecycle). Used by the Ask Puntt Projects feed so the shared chat renders
   * with full fidelity AND the project events appear inline. Each entry carries
   * a pre-rendered `node` so this component stays decoupled from feed cards.
   * Default `[]` → the standalone route + personal chats render exactly as
   * before (messages only).
   */
  timelineExtras?: { id: string; createdAt: string; node: ReactNode }[];
  /**
   * Visual frame for messages. "feed" (used by the Ask Puntt Projects feed)
   * renders each turn as a Slack-style avatar/name/timestamp row; "chat"
   * (default) is the classic chat layout. Forwarded to every ChatMessage.
   */
  messagePresentation?: "chat" | "feed";
  /** Display name for USER turns in feed presentation (e.g. the viewer). */
  feedActorName?: string;
  /** Avatar URL for USER turns in feed presentation. */
  feedActorAvatar?: string | null;
  /**
   * Project members for resolving per-message authors in feed presentation
   * (`message.userId`). When omitted, feed user rows fall back to the viewer
   * props above.
   */
  feedMembers?: {
    _id: string;
    name?: string;
    email?: string;
    avatar?: string | null;
  }[];
  /**
   * When true, the embedded chat never opens a right pane (canvas / ticket /
   * project status) — it always renders as a single chat-only column. The Ask
   * Puntt Projects feed passes this so the project workspace owns artifact
   * surfaces (Reviews / Documents panels + the dedicated review screen) instead
   * of the chat auto-switching its layout when the agent produces a canvas or a
   * review.
   */
  suppressRightPane?: boolean;
  /**
   * When the chat is embedded with `suppressRightPane` (project workspace),
   * opening a review ticket should delegate to the parent — e.g. mount
   * `CampaignReviewPane` — instead of the chat's internal right pane.
   */
  onProjectReviewOpen?: (ticketId: string) => void;
  /**
   * When embedded in the project workspace, opening a canvas document from
   * the campaign Documents rail delegates to the parent viewer.
   */
  onProjectCanvasOpen?: (messageId: string) => void;
  /** Fired when the chat's artifact list changes (embedded project workspace). */
  onArtifactsChange?: (artifacts: ChatArtifact[]) => void;
} = {}) {
  // Loose param read so the component works both as the `ask-puntt/$chatId`
  // route component AND when embedded under a different route (e.g. the
  // campaign workspace), where the strict route hook would not match.
  const looseParams = useParams({ strict: false }) as {
    chatId?: string;
    ticketId?: string;
    commentId?: string;
    messageId?: string;
  };
  const chatId = chatIdProp ?? looseParams.chatId ?? "";
  /** Standalone route component (not embedded in project workspace). */
  const isRouteDriven = chatIdProp == null;
  const urlSearch = useSearch({ strict: false }) as ChatSearch;
  const navigate = useNavigate();
  /** Campaign / project workspace embed — hide duplicate header actions. */
  const isEmbeddedInProjectWorkspace = chatIdProp != null;
  const queryClient = useQueryClient();
  const [waitingForAI, setWaitingForAI] = useState(false);
  // Stays true from the start of an agent turn until the user manually
  // scrolls or sends again — covers the canvas-pane layout shift that fires
  // after waitingForAI has already cleared.
  const [holdAutoScroll, setHoldAutoScroll] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  // Tracks the most recently pending message ID so agentSteps merge still works
  // after pendingMessageId is cleared (prevents the flash-on-completion bug).
  const lastPendingIdRef = useRef<string | null>(null);
  // True while a background canvas task is still writing — keeps the streaming
  // poll alive after the text response is complete so the full canvas renders.
  const [canvasPolling, setCanvasPolling] = useState(false);

  const [reviewPolling, setReviewPolling] = useState(false);
  // True from the moment the user presses Stop until the server stops
  // reporting an in-flight review. See `shouldShowStopButton`.
  const [stopRequested, setStopRequested] = useState(false);
  const allTicketsReviewCompleteRef = useRef(false);
  // True when the server has a pending assistant message that the current
  // session didn't create — i.e. the post-review summary placeholder appeared
  // in MongoDB while waitingForAI was already false. Stored as state so that
  // updates (via the effect below) trigger a re-render and cause refetchInterval
  // to flip to FULL_POLL_MS for the duration of the summary generation.
  const [serverHasPendingMessage, setServerHasPendingMessage] = useState(false);

  // Full document poll: 1s until we find the pending message, then slow to 3s.
  //
  // Keep polling during review limbo (viewTicketId stamped, reviewStatus null)
  // until the post-review summary lands. Uses `isReviewUiInLimbo` so long
  // single-ticket reviews keep polling past the 15m ticket-age cap — otherwise
  // Key Findings from `update_chat_details` never reach Chat Details. Multi-
  // ticket batches still age-cap. See `isReviewUiInLimbo` above.
  // Read by history refetchInterval: when the WebSocket is delivering fresh
  // stream patches, skip the 3s full-history poll (stream merge already
  // updates localMessages). Declared before the query so the interval
  // closure can read it; written after useAskPunttStream below.
  const wsStreamFreshRef = useRef(false);

  const {
    data: chatData,
    isLoading,
    dataUpdatedAt,
  } = useAskPunttChatHistory(chatId, {
    refetchInterval: (q) => {
      if (waitingForAI) {
        // While WS is fresh and we already have a pending message id, the
        // stream merge path owns UI updates — avoid a parallel 3s history poll.
        if (pendingMessageId && wsStreamFreshRef.current) return false;
        return pendingMessageId ? FULL_POLL_MS : STREAM_POLL_MS;
      }
      if (canvasPolling || reviewPolling || serverHasPendingMessage) {
        return FULL_POLL_MS;
      }
      const data = q.state.data;
      const messages = (data?.messages ?? []) as AskPunttMessageExt[];
      const chatReviewStatus = data?.chat?.reviewStatus ?? null;
      const viewTicketCreatedAt =
        getLatestViewTicketAssistantCreatedAt(messages);
      const viewTicketAssistantAgeMs = viewTicketCreatedAt
        ? Math.max(0, Date.now() - new Date(viewTicketCreatedAt).getTime())
        : null;
      if (allTicketsReviewCompleteRef.current) {
        if (chatReviewStatus) return FULL_POLL_MS;
        if (isPostReviewSummaryPending(messages)) return FULL_POLL_MS;
        if (hasViewTicketIdOnLastAssistant(messages)) {
          return FULL_POLL_MS;
        }
      }
      const inLimbo = isReviewUiInLimbo({
        hasViewTicketIdOnLastAssistant:
          hasViewTicketIdOnLastAssistant(messages),
        serverReviewStatus: data?.chat?.reviewStatus ?? null,
        viewTicketAssistantAgeMs,
        isMultiTicketReview: collectReviewTicketIds(messages).length > 1,
      });
      return inLimbo ? FULL_POLL_MS : false;
    },
  });

  const streamingMessageId =
    pendingMessageId ?? (canvasPolling ? lastPendingIdRef.current : null);

  // WebSocket push streaming is the PRIMARY transport. Provides near-real-time
  // updates when a WebSocket connection is available and FRESH; it has its own
  // internal 500ms polling fallback that re-arms automatically if the socket is
  // down or goes half-open (silent). `wsConnected` here means OPEN *and*
  // delivering pushes within WS_STALE_MS — not merely OPEN — so it is safe to
  // gate the redundant HTTP poll below on it without risking a stall.
  const {
    streamingState: wsStreamingState,
    wsConnected,
    lastWsPushAt,
  } = useAskPunttStream(chatId, streamingMessageId, {
    enabled: !!(streamingMessageId && (waitingForAI || canvasPolling)),
  });

  wsStreamFreshRef.current =
    !!wsConnected &&
    lastWsPushAt != null &&
    Date.now() - lastWsPushAt <= WS_FRESHNESS_MS;

  // Lightweight streaming poll: only the pending message's streaming fields.
  // This is now a TRUE FALLBACK — it only runs when the WebSocket is NOT
  // delivering (`!wsConnected`). While the WS is fresh it stays OFF, so a
  // healthy session streams over the socket with no parallel HTTP polling
  // (previously this polled continuously alongside the WS, defeating it).
  const { data: streamingState } = useAskPunttStreamingState(
    chatId,
    streamingMessageId,
    {
      enabled: !!(
        streamingMessageId &&
        (waitingForAI || canvasPolling) &&
        !wsConnected
      ),
      refetchInterval: STREAM_POLL_MS,
    },
  );

  // Prefer the real-time WS stream over the polling-based one, but only while
  // it is FRESH. If the socket goes half-open after one push (no close event,
  // so no reconnect / fallback re-arm), a permanent `wsStreamingState ?? …`
  // preference would freeze the UI on the last WS payload and shadow the
  // fresher 500ms poll forever. Prefer WS only within WS_FRESHNESS_MS of the
  // last push; after that, defer to the RQ poll (the secondary safety net).
  const mergedStreamingState =
    wsStreamingState &&
    lastWsPushAt != null &&
    Date.now() - lastWsPushAt <= WS_FRESHNESS_MS
      ? wsStreamingState
      : (streamingState ?? wsStreamingState);
  const sendMessage = useSendAskPunttMessage(chatId);
  const abortMessage = useAbortAskPunttMessage(chatId);
  const { mutate: markChatRead } = useMarkAskPunttChatRead(chatId);
  const markArtifactSeenMutation = useMarkAskPunttArtifactSeen(chatId);
  const lastMarkedReadReceiptRef = useRef<string | null>(null);
  const analytics = useAnalytics("ask_puntt_chat");
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);

  // --- Instrumentation: time-to-first-response ---
  const messageSentAtRef = useRef<number | null>(null);
  const ttfrFiredRef = useRef(false);

  // --- Streaming rAF coalescing ---
  // Stores the latest pending patch so the rAF callback always flushes the
  // most recent state, coalescing multiple rapid streaming updates into one
  // setLocalMessages call per animation frame.
  const pendingRafPatchRef = useRef<{
    activeId: string;
    state: StreamingState;
  } | null>(null);
  const rafFlushIdRef = useRef<number | null>(null);

  // --- Instrumentation: session depth ---
  const sessionStartRef = useRef(Date.now());
  const messageCountRef = useRef(0);
  const workflowTypeRef = useRef<string | undefined>(undefined);
  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  // Role-gated: OPS doesn't see connectors. See `canUseAskPunttConnectors`
  // in `utils/auth.ts` for the rationale; matches the home.tsx derivation
  // and the `ask.puntt.connectors.spec.ts > Not visible for OPS` contract.
  const availableConnectors: ConnectorOption[] = useMemo(
    () =>
      canUseAskPunttConnectors()
        ? (guidelinesQuery.data?.askPunttConnectors ?? [])
            .filter((c) => c.enabled && c.connected)
            .map((c) => ({
              id: c.connectorId,
              label: `${c.name} search`,
              icon: CONNECTOR_ICONS[c.connectorId] ?? "🔗",
            }))
        : [],
    [guidelinesQuery.data?.askPunttConnectors],
  );

  const [inputMessage, setInputMessage] = useState("");
  // Active @mention actor ids for the feed composer (members + "puntt").
  // Display + attribution only — does not change AI triggering.
  const [mentions, setMentions] = useState<string[]>([]);
  const [mentionEmails, setMentionEmails] = useState<string[]>([]);
  const [sectionContext, setSectionContext] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [localMessages, setLocalMessages] = useState<AskPunttMessageExt[]>([]);
  // Refs for stable reads inside useCallback / rAF closures — updated
  // synchronously in render so they are always current before any callback fires.
  const localMessagesRef = useRef<AskPunttMessageExt[]>([]);
  localMessagesRef.current = localMessages;
  // dataUpdatedAt and chatData change on every refetch (up to every 500ms
  // during streaming). Using refs avoids adding them to handleSend's dep array,
  // which would recreate the callback on every poll tick and re-render memoized
  // ChatMessage children for no user-visible reason.
  const dataUpdatedAtRef = useRef(dataUpdatedAt);
  dataUpdatedAtRef.current = dataUpdatedAt;
  const chatDataRef = useRef(chatData);
  chatDataRef.current = chatData;
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
    localPreviewUrls,
  } = useAttachedFiles({
    onUploadError: (err) => {
      if (err instanceof PasswordProtectedPdfError) {
        setProtectedPdfFileName(err.fileName);
      }
      analytics.capture("ask_puntt_upload_error", {
        chat_id: chatId,
        ...errorAnalyticsPayload(err),
      });
    },
  });
  const [protectedPdfFileName, setProtectedPdfFileName] = useState<
    string | null
  >(null);
  const [passwordPdfViewerWarning, setPasswordPdfViewerWarning] = useState<
    string | null
  >(null);
  const handlePasswordRequired = useCallback((fileName: string) => {
    setPasswordPdfViewerWarning(fileName);
  }, []);
  // Page-wide drag-and-drop is owned by the outer AskPunttDropZone wrapper
  // below. Only retain the clipboard paste handler here so users can still
  // paste screenshots / images directly into the composer.
  const { handlePaste } = useFileDrop({ onDrop: addFiles });
  const [canvasDismissed, setCanvasDismissed] = useState(false);
  const [ticketDismissed, setTicketDismissed] = useState(false);
  // Standalone multi-review workspace: browse (chat + reviews rail) vs review
  // (narrow chat + CampaignReviewPane). Mirrors the project campaign shell.
  const [activeReviewTicketId, setActiveReviewTicketId] = useState<
    string | null
  >(null);
  const [reviewChatCollapsed, setReviewChatCollapsed] = useState(false);
  const [chatDetailsSectionOpen, setChatDetailsSectionOpen] = useState(true);
  // Manual selection of an older canvas via the chat-history tool-result
  // row. `null` = "auto-latest" (mirrors how tickets default to the most
  // recent on `selectedReviewIndex = -1`). Resets on chat switch via the
  // effect near `handleNextReview` so older selections don't leak across
  // routes.
  const [selectedCanvasMessageId, setSelectedCanvasMessageId] = useState<
    string | null
  >(null);
  const initialLoadRef = useRef(true);
  const waitingSinceRef = useRef<number | null>(null);
  const dataAtWaitStartRef = useRef<number | null>(null);
  const prevDataUpdatedAtRef = useRef<number>(0);

  // Reset local message state when switching chats so the previous thread's
  // messages and polling flags cannot leak into the newly opened chat.
  useEffect(() => {
    initialLoadRef.current = true;
    prevDataUpdatedAtRef.current = 0;
    dataAtWaitStartRef.current = null;
    waitingSinceRef.current = null;
    lastPendingIdRef.current = null;
    setLocalMessages([]);
    setWaitingForAI(false);
    setPendingMessageId(null);
    setCanvasPolling(false);
    setReviewPolling(false);
    setServerHasPendingMessage(false);
    setHoldAutoScroll(false);
    setCanvasDismissed(false);
    setTicketDismissed(false);
  }, [chatId]);

  useEffect(() => {
    if (waitingForAI) {
      waitingSinceRef.current = Date.now();
    } else {
      waitingSinceRef.current = null;
      dataAtWaitStartRef.current = null;
      setPendingMessageId(null);
    }
  }, [waitingForAI]);

  // Keep lastPendingIdRef in sync so we can merge agentSteps even after
  // pendingMessageId is cleared to null.
  useEffect(() => {
    if (pendingMessageId) {
      lastPendingIdRef.current = pendingMessageId;
    }
  }, [pendingMessageId]);

  // Sync server messages with local state and detect completed assistant
  // responses.  We use `dataUpdatedAt` (changes on every successful refetch)
  // instead of `chatData?.messages` alone — TanStack Query's structural
  // sharing can return the same array reference when the deep content hasn't
  // changed from its perspective, which would prevent the effect from firing.
  useEffect(() => {
    if (!chatData?.messages) return;
    if (chatData.chat?._id && chatData.chat._id !== chatId) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      const messages = chatData.messages;
      setLocalMessages(messages);
      const last = messages[messages.length - 1];
      if (
        messages.length > 0 &&
        (last.role === "user" ||
          (last.role === "assistant" &&
            (last.status === "pending" || !last.content) &&
            !isReviewStatusMessage(last)))
      ) {
        setWaitingForAI(true);
      }
      return;
    }

    const dataChanged = dataUpdatedAt !== prevDataUpdatedAtRef.current;
    if (dataChanged) {
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      setLocalMessages((prev) => {
        const mergeId = pendingMessageId ?? lastPendingIdRef.current;
        if (!mergeId) {
          return mergeServerMessagesWithLocal(chatData.messages, prev);
        }
        const localPending = prev.find((m) => m._id === mergeId);
        if (!localPending) {
          return mergeServerMessagesWithLocal(chatData.messages, prev);
        }
        return mergeServerMessagesWithLocal(
          chatData.messages.map((m) =>
            m._id === mergeId ? mergeMessageStatus(localPending, m) : m,
          ),
          prev,
        );
      });
    }

    if (!waitingForAI) return;

    if (
      waitingSinceRef.current &&
      Date.now() - waitingSinceRef.current > POLL_TIMEOUT_MS
    ) {
      setWaitingForAI(false);
      return;
    }

    if (!dataChanged) return;

    // Skip response detection until we get fresh data after waiting started.
    // Without this, the effect fires with stale chatData (still showing the
    // previous assistant response) and immediately stops polling.
    if (
      dataAtWaitStartRef.current !== null &&
      dataUpdatedAt <= dataAtWaitStartRef.current
    ) {
      return;
    }

    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.content &&
      lastMsg.status !== "pending"
    ) {
      setWaitingForAI(false);
      dataAtWaitStartRef.current = null;
      if (lastMsg.viewTicketId) {
        setReviewPolling(true);
      }
      // The shared-chat agent mutated the project this turn — let the workspace
      // refetch so the docs panel + header reflect it without a manual refresh.
      // `projectMutated` lives on the FE-local Ext type, not the base schema.
      if ((lastMsg as AskPunttMessageExt).projectMutated) {
        onProjectMutated?.();
      }
    }
    // Detect pending assistant message ID for lightweight polling
    if (waitingForAI && dataChanged && !pendingMessageId) {
      const pendingMsg = findPendingAssistantForStreaming(chatData.messages);
      if (pendingMsg?._id) {
        setPendingMessageId(pendingMsg._id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, waitingForAI]);

  // Detect the pending assistant message ID for lightweight streaming poll
  useEffect(() => {
    if (!waitingForAI || !chatData?.messages || pendingMessageId) return;
    const pendingMsg = findPendingAssistantForStreaming(chatData.messages);
    if (pendingMsg?._id) {
      setPendingMessageId(pendingMsg._id);
    }
  }, [chatData?.messages, waitingForAI, pendingMessageId]);

  // Track whether the server has a pending assistant message we didn't create.
  // Applies when waitingForAI is false — covers the post-review summary
  // placeholder that appears after the review finishes without a user prompt.
  useEffect(() => {
    if (waitingForAI) {
      setServerHasPendingMessage(false);
      return;
    }
    setServerHasPendingMessage(
      isPostReviewSummaryPending(chatData?.messages ?? []),
    );
  }, [chatData?.messages, waitingForAI]);

  // Merge lightweight streaming state into localMessages for faster UI updates.
  // Uses the merged stream (WebSocket push preferred over polling fallback) so
  // updates arrive as fast as the push Lambda can deliver them through the
  // WebSocket without waiting for the next 500ms poll tick.
  //
  // Mid-stream patches are coalesced via requestAnimationFrame so multiple
  // rapid WebSocket pushes within a single frame collapse into one
  // setLocalMessages call. Final-content updates (streamingContent cleared,
  // content set) are always flushed synchronously so the UI never delays the
  // completed message behind a rAF.
  useEffect(() => {
    const state = mergedStreamingState;
    if (!state) return;

    // Drive canvasPolling: start when backend signals canvas is generating,
    // stop when canvasLoading clears (canvas complete or errored).
    if (state.canvasLoading && !canvasPolling) {
      setCanvasPolling(true);
    } else if (!state.canvasLoading && canvasPolling) {
      setCanvasPolling(false);
    }

    // After the text response is done, pendingMessageId is null but we still
    // need to apply canvas updates — use lastPendingIdRef for that phase.
    const activeId = pendingMessageId ?? lastPendingIdRef.current;
    if (!activeId) return;

    // Instrumentation: time-to-first-response
    if (
      !ttfrFiredRef.current &&
      messageSentAtRef.current &&
      (state.streamingContent || state.content)
    ) {
      const ttfrMs = Date.now() - messageSentAtRef.current;
      analytics.capture("ask_puntt_time_to_first_response", {
        chat_id: chatId,
        ttfr_ms: ttfrMs,
        workflow_type: chatData?.chat?.workflowType,
      });
      ttfrFiredRef.current = true;
    }

    // Build and apply a localMessages patch for `targetId` from `patchState`.
    // Only writes fields the streaming payload actually provides — unconditionally
    // overwriting canvasContent/canvasLoading with undefined was causing the
    // right-pane canvas to unmount and re-mount on every poll tick.
    const applyPatch = (patchState: StreamingState, targetId: string): void => {
      if (typeof performance !== "undefined" && performance.mark) {
        performance.mark("ask_puntt_stream_frame_start");
      }
      setLocalMessages((prev) =>
        prev.map((msg) => {
          if (msg._id !== targetId) return msg;
          const patch: Partial<AskPunttMessageExt> = {};
          if (patchState.status !== undefined)
            patch.status = patchState.status as AskPunttMessage["status"];
          if (patchState.streamingContent !== undefined)
            patch.streamingContent = patchState.streamingContent;
          if (patchState.canvasLoading !== undefined)
            patch.canvasLoading = patchState.canvasLoading;
          if (patchState.suggestedResponses !== undefined)
            patch.suggestedResponses = patchState.suggestedResponses;
          if (patchState.content) patch.content = patchState.content;
          if (patchState.agentSteps !== undefined) {
            // Prefer longer local steps while streaming so a stale Dynamo
            // snapshot (text-only publish mid-tool) cannot wipe newer steps
            // already merged from history/WS. Terminal status trusts server.
            const preferred = preferAgentSteps(
              msg.agentSteps,
              patchState.agentSteps as AskPunttMessage["agentSteps"],
              patchState.status as AskPunttMessage["status"] | undefined,
            );
            if (preferred !== undefined) patch.agentSteps = preferred;
          }
          if (patchState.searchProgress?.length)
            patch.searchProgress =
              patchState.searchProgress as AskPunttMessage["searchProgress"];
          if (patchState.canvasContent)
            patch.canvasContent =
              patchState.canvasContent as AskPunttMessage["canvasContent"];
          if (patchState.canvasChangedSectionIds?.length)
            patch.canvasChangedSectionIds = patchState.canvasChangedSectionIds;
          if (patchState.viewTicketId)
            patch.viewTicketId = patchState.viewTicketId;
          if (patchState.projectData)
            patch.projectData =
              patchState.projectData as AskPunttMessage["projectData"];
          if (patchState.projectLoading !== undefined)
            patch.projectLoading = patchState.projectLoading;
          if (patchState.contentBlocks)
            patch.contentBlocks =
              patchState.contentBlocks as AskPunttMessage["contentBlocks"];
          return { ...msg, ...patch };
        }),
      );
      if (
        typeof performance !== "undefined" &&
        performance.mark &&
        performance.measure
      ) {
        try {
          performance.mark("ask_puntt_stream_frame_end");
          performance.measure(
            "ask_puntt_stream_frame_ms",
            "ask_puntt_stream_frame_start",
            "ask_puntt_stream_frame_end",
          );
        } catch {
          // Missing marks — ignore.
        }
      }
    };

    // Flush synchronously when streaming is complete (streamingContent cleared,
    // final content present) so the completed message is never delayed.
    const isFinal = !state.streamingContent && !!state.content;
    if (isFinal) {
      if (rafFlushIdRef.current != null) {
        cancelAnimationFrame(rafFlushIdRef.current);
        rafFlushIdRef.current = null;
      }
      pendingRafPatchRef.current = null;
      applyPatch(state, activeId);
      return;
    }

    // Coalesce mid-stream patches: store the latest state in the ref so the
    // rAF callback always flushes the most recent data, then schedule a flush
    // if one is not already pending.
    pendingRafPatchRef.current = { activeId, state };
    if (rafFlushIdRef.current == null) {
      rafFlushIdRef.current = requestAnimationFrame(() => {
        rafFlushIdRef.current = null;
        const pending = pendingRafPatchRef.current;
        if (!pending) return;
        pendingRafPatchRef.current = null;
        applyPatch(pending.state, pending.activeId);
      });
    }
  }, [mergedStreamingState, pendingMessageId, canvasPolling]);

  // Cancel any pending streaming rAF on unmount so the callback never fires
  // against an unmounted component.
  useEffect(() => {
    return () => {
      if (rafFlushIdRef.current != null) {
        cancelAnimationFrame(rafFlushIdRef.current);
        rafFlushIdRef.current = null;
      }
    };
  }, []);

  // Keep workflowType ref in sync so the unmount cleanup reads fresh data
  useEffect(() => {
    workflowTypeRef.current = chatData?.chat?.workflowType;
  }, [chatData?.chat?.workflowType]);

  const DEFAULT_PAGE_TITLE =
    "Puntt - Manual reviews. Automated for marketing teams.";

  useEffect(() => {
    analytics.capture("ask_puntt_chat_viewed", { chat_id: chatId });

    // Instrumentation: session depth on unmount
    return () => {
      const durationMs = Date.now() - sessionStartRef.current;
      analytics.capture("ask_puntt_session_ended", {
        chat_id: chatId,
        duration_ms: durationMs,
        message_count: messageCountRef.current,
        workflow_type: workflowTypeRef.current,
      });
    };
  }, [chatId]);

  // Set page title to "Session name | Puntt" when a chat is loaded.
  useEffect(() => {
    const title = chatData?.chat?.title;
    if (title) {
      document.title = `${title} | Puntt`;
    }
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
    };
  }, [chatData?.chat?.title]);

  // Detect the latest canvas content from messages.
  //
  // The right-pane 2-pane ternary picks ``activeCanvas`` before
  // ``effectiveViewTicketId`` (see ``rightPane2PaneInner`` below),
  // so this memo is load-bearing for "which surface wins the right
  // pane when both a stale canvas and a fresh review exist."
  //
  // Bug guarded against: if the user's most-recent assistant turn is
  // a NEW review (carries ``viewTicketId`` and no ``canvasContent``),
  // we explicitly return ``null`` so the older canvas from a prior
  // turn doesn't keep covering the new review. Without this gate the
  // canvas walks back through history and grabs the first
  // canvas-bearing message it finds, which means: ask for a review →
  // ticket pane → ask for ROI canvas → canvas pane → ask for another
  // review → ticket lands but the canvas stays on top forever.
  //
  // The two other places that pick between canvas and ticket — the
  // collapsed-pane label and the persisted-layout storage key —
  // already prefer the ticket when both exist; this brings the body
  // ternary in line with that intent.
  //
  // We DO NOT clear the canvas when the latest assistant turn is a
  // non-review message (e.g. a plain follow-up question), because in
  // that case the user is still likely consuming the canvas and just
  // having a side conversation about it. The trigger is specifically
  // "a new review landed without bringing its own canvas."
  const legacyActiveCanvas = useMemo((): {
    canvas: CanvasContent & { changedSectionIds?: string[] };
    skill?: string;
    messageId: string;
  } | null => {
    if (canvasDismissed) return null;
    // Explicit selection (user clicked an older canvas tool-result row)
    // wins over both the suppress-for-fresh-review guard AND the
    // latest-message auto-pick. The selection resets on chat switch and
    // is otherwise sticky until the user clears or selects elsewhere.
    if (selectedCanvasMessageId) {
      const selected = localMessages.find(
        (m) =>
          m._id === selectedCanvasMessageId &&
          m.role === "assistant" &&
          m.canvasContent &&
          (m.canvasContent as CanvasContent).blocks?.length > 0,
      );
      if (selected) {
        return {
          canvas: {
            ...(selected.canvasContent as CanvasContent),
            changedSectionIds: selected.canvasChangedSectionIds,
          },
          skill: selected.activeSkill ?? undefined,
          messageId: selected._id,
        };
      }
      // Selected message lost its canvas (poll merge or extreme race) —
      // fall through to the latest-canvas path so the user isn't stranded
      // on an empty pane.
    }
    // Latest-turn check: only suppress a stale canvas when the
    // newest completed assistant message is a review (viewTicketId
    // present, no canvas of its own). Pending and user messages are
    // skipped so an in-flight turn doesn't flicker the right pane.
    let suppressForFreshReview = false;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role !== "assistant") continue;
      if (msg.status === "pending" || !msg.content) continue;
      if (
        msg.viewTicketId &&
        !(
          msg.canvasContent &&
          (msg.canvasContent as CanvasContent).blocks?.length > 0
        )
      ) {
        suppressForFreshReview = true;
      }
      break;
    }
    if (suppressForFreshReview) return null;
    // Find the last assistant message with canvasContent
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return {
          canvas: {
            ...(msg.canvasContent as CanvasContent),
            changedSectionIds: msg.canvasChangedSectionIds,
          },
          skill: msg.activeSkill ?? undefined,
          messageId: msg._id,
        };
      }
    }
    return null;
  }, [localMessages, canvasDismissed, selectedCanvasMessageId]);

  // Instrumentation: canvas rendered (tracked per messageId to avoid duplicate events)
  const canvasTrackedRef = useRef<Set<string>>(new Set());

  // Check if any message has canvas content (regardless of dismissed state)
  const hasCanvasContent = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return true;
      }
    }
    return false;
  }, [localMessages]);

  // Id of the last message that actually renders in the feed. Hidden
  // (synthesized-transcript) messages are skipped so a trailing hidden message
  // doesn't steal the "fully expanded" slot from the newest visible one.
  const lastVisibleMessageId = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      if (!isHiddenMessage(localMessages[i])) return localMessages[i]._id;
    }
    return undefined;
  }, [localMessages]);

  // Detect canvas loading state (agent is generating canvas content).
  // Not gated on waitingForAI — canvas can still be generating after the text
  // response is saved, so we rely on canvasPolling for the post-text phase.
  const isCanvasLoading = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(
      lastMsg?.role === "assistant" &&
      (lastMsg.canvasLoading || canvasPolling)
    );
  }, [localMessages, canvasPolling]);

  // Detect project loading state (agent is generating project data)
  const isProjectLoading = useMemo(() => {
    if (!waitingForAI) return false;
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(lastMsg?.role === "assistant" && lastMsg.projectLoading);
  }, [localMessages, waitingForAI]);

  // Collect all viewTicketIds in chronological order (de-duplicated). Drives
  // the "Review N of M" navigation in the right pane so users can revisit
  // earlier reviews when multiple sequential reviews exist in one chat.
  const allReviewTicketIds = useMemo(
    () => collectReviewTicketIds(localMessages),
    [localMessages],
  );

  const hasViewTicketId = allReviewTicketIds.length > 0;

  const isMultiReview = allReviewTicketIds.length > 1;
  // Findings-only `projectData` (Key Findings stub from #4131) must NOT
  // disable standalone multi-review workspace — only a real staged project
  // plan should. Revert-kill: `!chat.projectData` treated findings stubs as
  // projects and hid the multi-review Chat Details rail after findings wrote.
  const isStandaloneMultiReviewWorkspace =
    isMultiReview &&
    !suppressRightPane &&
    !hasProjectPlanStages(
      chatData?.chat?.projectData as AskPunttProjectData | null | undefined,
    ) &&
    chatData?.chat?.workflowType !== "project";

  const [selectedReviewIndex, setSelectedReviewIndex] = useState(-1);

  const reviewTicketTitleMap = useMemo(
    () => collectReviewTicketTitles(localMessages),
    [localMessages],
  );

  const useCoworkArtifactMode =
    !suppressRightPane && chatData?.chat?.workflowType !== "project";

  const { reviews: chatReviewSummariesList } = useChatReviewSummaries(
    allReviewTicketIds,
    reviewTicketTitleMap,
    // Browse-mode cards share the same query keys as the ticket viewer; poll
    // while a review is in flight so AI comment counts update without opening
    // the viewer (and without waiting for the 60s staleTime).
    { pollingEnabled: reviewPolling },
  );

  const reviewSummariesMap = useMemo(() => {
    const map = new Map<
      string,
      {
        thumbnailUrl?: string | null;
        aiCommentCount?: number;
        humanCommentCount?: number;
        versionNumber?: number;
        isReviewInFlight?: boolean;
      }
    >();
    for (const r of chatReviewSummariesList) {
      map.set(r.ticketId, {
        thumbnailUrl: r.thumbnailUrl,
        aiCommentCount: r.aiCommentCount,
        humanCommentCount: r.humanCommentCount,
        versionNumber: r.versionNumber,
        isReviewInFlight: r.isReviewInFlight,
      });
    }
    return map;
  }, [chatReviewSummariesList]);

  const allTicketsReviewComplete = useMemo(
    () =>
      areAllReviewTicketsComplete(allReviewTicketIds, chatReviewSummariesList),
    [allReviewTicketIds, chatReviewSummariesList],
  );

  useEffect(() => {
    allTicketsReviewCompleteRef.current = allTicketsReviewComplete;
  }, [allTicketsReviewComplete]);

  const artifactWorkspace = useChatArtifactWorkspace({
    messages: localMessages,
    titleMap: reviewTicketTitleMap,
    reviewSummaries: reviewSummariesMap,
    chatId,
    onArtifactOpened: (payload, source) => {
      markArtifactSeenMutation.mutate(payload);
      analytics.capture("ask_puntt_artifact_opened", {
        kind: payload.kind,
        ticket_id: payload.kind === "review" ? payload.ticketId : undefined,
        message_id: payload.kind === "canvas" ? payload.messageId : undefined,
        chat_id: chatId,
        source,
      });
    },
  });

  const tailReadReceiptKey = useMemo(
    () =>
      tailMessageReadReceiptKey(localMessages, (message) =>
        isReviewStatusMessage(message as AskPunttMessageExt),
      ),
    [localMessages],
  );
  const tailReadReceiptKeyRef = useRef(tailReadReceiptKey);
  tailReadReceiptKeyRef.current = tailReadReceiptKey;

  useEffect(() => {
    lastMarkedReadReceiptRef.current = null;
  }, [chatId]);

  const lastMessageAtInvalidatedRef = useRef<string | null>(null);
  useEffect(() => {
    lastMessageAtInvalidatedRef.current = null;
  }, [chatId]);
  useEffect(() => {
    const lastMessageAt = chatData?.chat?.lastMessageAt;
    if (!chatId || !lastMessageAt) return;
    if (lastMessageAtInvalidatedRef.current === lastMessageAt) return;
    lastMessageAtInvalidatedRef.current = lastMessageAt;
    invalidateAskPunttUnreadLists(queryClient);
  }, [chatData?.chat?.lastMessageAt, chatId, queryClient]);

  // Mark read when the chat is visible and the tail message is settled (not
  // while an assistant reply is still pending — completion bumps lastMessageAt
  // on the server even when the message id stays the same).
  useEffect(() => {
    if (!chatId || !tailReadReceiptKey) return;
    if (sendMessage.isPending) return;

    const markVisible = () => {
      if (document.visibilityState !== "visible") return;
      const receiptKey = tailReadReceiptKeyRef.current;
      if (!receiptKey || lastMarkedReadReceiptRef.current === receiptKey)
        return;
      lastMarkedReadReceiptRef.current = receiptKey;
      const messageId = receiptKey.split(":")[0];
      markChatRead({ messageId });
    };

    markVisible();
    document.addEventListener("visibilitychange", markVisible);
    return () => document.removeEventListener("visibilitychange", markVisible);
  }, [chatId, tailReadReceiptKey, markChatRead, sendMessage.isPending]);

  const unseenArtifactKeys = useMemo(
    () => new Set(chatData?.userSeen?.unseenArtifactKeys ?? []),
    [chatData?.userSeen?.unseenArtifactKeys],
  );

  const isArtifactUnreadForViewer = useCallback(
    (artifact: ChatArtifact) => isArtifactUnread(artifact, unseenArtifactKeys),
    [unseenArtifactKeys],
  );

  useEffect(() => {
    onArtifactsChange?.(artifactWorkspace.artifacts);
  }, [artifactWorkspace.artifacts, onArtifactsChange]);

  const activeCanvas = useCoworkArtifactMode
    ? artifactWorkspace.activeCanvas
    : legacyActiveCanvas;

  useEffect(() => {
    if (!activeCanvas) return;
    const messageId = activeCanvas.messageId;
    if (!canvasTrackedRef.current.has(messageId)) {
      analytics.capture("ask_puntt_canvas_rendered", {
        chat_id: chatId,
        output_type: activeCanvas.canvas.outputType,
        block_count: activeCanvas.canvas.blocks?.length ?? 0,
        skill: activeCanvas.skill,
      });
      canvasTrackedRef.current.add(messageId);
    }
  }, [activeCanvas, chatId]);

  const openStandaloneReview = useCallback(
    (ticketId: string) => {
      const idx = allReviewTicketIds.indexOf(ticketId);
      if (idx < 0) return;
      setSelectedReviewIndex(idx);
      setActiveReviewTicketId(ticketId);
      setTicketDismissed(false);
    },
    [allReviewTicketIds],
  );

  const syncChatSearch = useCallback(
    (patch: Partial<ChatSearch>, options?: { replace?: boolean }) => {
      if (!isRouteDriven || !chatId) return;
      navigate({
        ...buildChatPath(chatId, { ...urlSearch, ...patch }),
        replace: options?.replace ?? true,
      });
    },
    [isRouteDriven, chatId, navigate, urlSearch],
  );

  const navigateChatReview = useCallback(
    (
      ticketId: string,
      commentId?: string | null,
      options?: { replace?: boolean },
    ) => {
      if (!isRouteDriven || !chatId) return;
      navigate({
        ...buildChatReviewPath(
          chatId,
          ticketId,
          commentId ?? undefined,
          urlSearch.version,
        ),
        replace: options?.replace ?? false,
      } as never);
    },
    [isRouteDriven, chatId, navigate, urlSearch.version],
  );

  const openStandaloneReviewWithUrl = useCallback(
    (ticketId: string) => {
      openStandaloneReview(ticketId);
      navigateChatReview(ticketId, null, { replace: false });
    },
    [openStandaloneReview, navigateChatReview],
  );

  const clearChatReviewUrl = useCallback(() => {
    if (!isRouteDriven || !chatId) return;
    navigate({
      ...buildChatPath(chatId),
      replace: true,
    });
  }, [isRouteDriven, chatId, navigate]);

  const navigateChatCanvas = useCallback(
    (messageId: string, options?: { replace?: boolean }) => {
      if (!isRouteDriven || !chatId) return;
      navigate({
        ...buildChatCanvasPath(chatId, messageId),
        replace: options?.replace ?? false,
      });
    },
    [isRouteDriven, chatId, navigate],
  );

  useEffect(() => {
    if (!activeReviewTicketId) return;
    const idx = allReviewTicketIds.indexOf(activeReviewTicketId);
    if (idx >= 0) setSelectedReviewIndex(idx);
  }, [activeReviewTicketId, allReviewTicketIds]);

  // Sticky auto-advance: jump to the newest review only when the user is
  // already on the latest one (or has no selection yet). Preserves manual
  // navigation when the user has gone back to an earlier review.
  // Standalone multi-review workspace stays in browse mode until the user
  // opens a review from the rail or a chat artifact row.
  useEffect(() => {
    if (useCoworkArtifactMode) return;
    if (isStandaloneMultiReviewWorkspace) return;
    if (allReviewTicketIds.length === 0) return;
    setSelectedReviewIndex((prev) => {
      if (prev < 0 || prev === allReviewTicketIds.length - 1) {
        return allReviewTicketIds.length - 1;
      }
      return prev;
    });
  }, [
    allReviewTicketIds.length,
    isStandaloneMultiReviewWorkspace,
    useCoworkArtifactMode,
  ]);

  const activeViewTicketId = useMemo((): string | null => {
    if (ticketDismissed) return null;
    if (isStandaloneMultiReviewWorkspace) {
      return activeReviewTicketId;
    }
    if (
      selectedReviewIndex >= 0 &&
      selectedReviewIndex < allReviewTicketIds.length
    ) {
      return allReviewTicketIds[selectedReviewIndex];
    }
    return null;
  }, [
    ticketDismissed,
    isStandaloneMultiReviewWorkspace,
    activeReviewTicketId,
    selectedReviewIndex,
    allReviewTicketIds,
  ]);

  const coworkActiveViewTicketId = useCoworkArtifactMode
    ? artifactWorkspace.activeViewTicketId
    : activeViewTicketId;

  const handlePrevReview = useCallback(() => {
    setSelectedReviewIndex((i) => {
      const next = Math.max(0, i - 1);
      const id = allReviewTicketIds[next];
      if (useCoworkArtifactMode && id) {
        artifactWorkspace.openArtifact(
          { kind: "review", ticketId: id },
          "sidebar",
        );
      } else if (
        isStandaloneMultiReviewWorkspace &&
        activeReviewTicketId &&
        id
      ) {
        setActiveReviewTicketId(id);
      }
      return next;
    });
  }, [
    useCoworkArtifactMode,
    artifactWorkspace,
    isStandaloneMultiReviewWorkspace,
    activeReviewTicketId,
    allReviewTicketIds,
  ]);

  const handleNextReview = useCallback(() => {
    setSelectedReviewIndex((i) => {
      const next = Math.min(allReviewTicketIds.length - 1, i + 1);
      const id = allReviewTicketIds[next];
      if (useCoworkArtifactMode && id) {
        artifactWorkspace.openArtifact(
          { kind: "review", ticketId: id },
          "sidebar",
        );
      } else if (
        isStandaloneMultiReviewWorkspace &&
        activeReviewTicketId &&
        id
      ) {
        setActiveReviewTicketId(id);
      }
      return next;
    });
  }, [
    allReviewTicketIds,
    useCoworkArtifactMode,
    artifactWorkspace,
    isStandaloneMultiReviewWorkspace,
    activeReviewTicketId,
  ]);

  // Canvas analogue of `allReviewTicketIds`: chronological list of
  // assistant message ids whose `canvasContent.blocks` is non-empty.
  // Drives D6 single-canvas-chat gating and Context payload.
  const canvasMessageIds = useMemo(
    () => collectCanvasMessageIds(localMessages),
    [localMessages],
  );

  // Sticky selection: clear when the list empties OR the selected
  // message dropped out (poll merge could remove it). Otherwise preserve
  // the user's explicit selection across new arrivals — unlike the
  // ticket sticky-advance which advances index-on-latest, canvas tracks
  // by stable message id so new arrivals don't shift it.
  useEffect(() => {
    if (canvasMessageIds.length === 0) {
      setSelectedCanvasMessageId(null);
      return;
    }
    setSelectedCanvasMessageId((prev) => {
      if (prev === null) return null;
      if (!canvasMessageIds.includes(prev)) return null;
      return prev;
    });
  }, [canvasMessageIds]);

  // Reset selection on chat switch so the previous chat's selected
  // canvas message id can't leak into a new chat where it's meaningless.
  useEffect(() => {
    setSelectedCanvasMessageId(null);
  }, [chatId]);

  // D8 streaming-suppression predicate: true iff the in-flight turn is
  // producing a new artifact (ticket or canvas). Logic is extracted to
  // `computeIsAnotherArtifactStreaming` so it can be unit-tested directly.
  const isAnotherArtifactStreaming = useMemo(
    () =>
      computeIsAnotherArtifactStreaming(
        localMessages,
        ARTIFACT_PRODUCING_TOOLS,
      ),
    [localMessages],
  );

  // Extracted activate handlers (see `chatUtils.ts`). Stale ticket / canvas
  // ids no-op silently — no analytics, no state mutation — so a click on a
  // row whose backing artifact disappeared (deleted ticket, canvas poll-merge
  // wipe) is a non-event from the chat's perspective.
  const onActivateTicket = useMemo(() => {
    if (suppressRightPane && onProjectReviewOpen) {
      return (ticketId: string) => {
        const idx = allReviewTicketIds.indexOf(ticketId);
        if (idx >= 0) {
          setSelectedReviewIndex(idx);
          setTicketDismissed(false);
        }
        onProjectReviewOpen(ticketId);
        analytics.capture("ask_puntt_previous_artifact_clicked", {
          artifact: "ticket",
          ticket_id: ticketId,
          chat_id: chatId,
        });
      };
    }
    if (isStandaloneMultiReviewWorkspace) {
      return (ticketId: string) => {
        const idx = allReviewTicketIds.indexOf(ticketId);
        if (idx < 0) return;
        openStandaloneReviewWithUrl(ticketId);
        analytics.capture("ask_puntt_previous_artifact_clicked", {
          artifact: "ticket",
          ticket_id: ticketId,
          chat_id: chatId,
        });
      };
    }
    return createActivateTicketHandler({
      allReviewTicketIds,
      setSelectedReviewIndex,
      setTicketDismissed,
      capture: analytics.capture,
      chatId,
    });
  }, [
    suppressRightPane,
    onProjectReviewOpen,
    isStandaloneMultiReviewWorkspace,
    openStandaloneReviewWithUrl,
    allReviewTicketIds,
    analytics,
    chatId,
  ]);

  const onActivateCanvas = useMemo(() => {
    const base = createActivateCanvasHandler({
      canvasMessageIds,
      setSelectedCanvasMessageId,
      setCanvasDismissed,
      capture: analytics.capture,
      chatId,
    });
    return (messageId: string) => {
      base(messageId);
      navigateChatCanvas(messageId, { replace: false });
    };
  }, [canvasMessageIds, analytics, chatId, navigateChatCanvas]);

  const activeCanvasMessageId = useMemo(
    () => activeCanvas?.messageId ?? null,
    [activeCanvas?.messageId],
  );

  // LLM-provided pane labels for 3-panel layout (e.g. "Brief" / "Deliverable")
  const activePaneLabels = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.paneLabels) return msg.paneLabels;
    }
    return undefined;
  }, [localMessages]);

  // Theatrical typewriter reveal for the Vitalora killer-flow demo
  // response. The hook self-gates by inspecting the latest assistant
  // message's shape (`isKillerFlowAssistantMessage`), so we only
  // need a coarse tenant-level enable flag here — production tenants
  // never land a killer-flow-shaped payload, so the hook is a no-op
  // for them in practice. We keep this gated on MG/demo as a safety
  // net so an accidental killer-flow payload on a real tenant doesn't
  // surprise anyone with a 90s typewriter.
  //
  // Declared here (rather than its historical position after every
  // useEffect) so `leftPaneProjectData` below can read
  // `isKillerFlowStreaming` as one of its in-flight signals — killer-
  // flow chats never set `chat.reviewStatus`, so without this the
  // synthesized stub would never fire for single-asset killer-flow
  // chats whose backend hasn't yet been updated to emit `projectData`.
  const killerStream = useKillerFlowStream({
    enabled: isMeaningfulGigsOrDemo(),
    messages: localMessages,
  });
  const isKillerFlowStreaming = killerStream.isStreaming;

  // Detect project data from chat-level or latest message
  const activeProjectData = useMemo((): AskPunttProjectData | null => {
    const chatLevel = chatData?.chat?.projectData as
      | AskPunttProjectData
      | null
      | undefined;
    if (chatLevel) return chatLevel;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.projectData) {
        return msg.projectData as AskPunttProjectData;
      }
    }
    return null;
  }, [chatData?.chat?.projectData, localMessages]);

  // ── Review status + first-review right-pane gating ──────────────────────
  // These derivations are hoisted above the layout switches (`use3PaneLayout`
  // / `has2PaneRight` below) so the first-review suppression can read them
  // without a use-before-define (TDZ) crash. They depend only on `chatData`,
  // `activeProjectData`, `isKillerFlowStreaming`, `isProjectLoading`, and
  // `localMessages`, all defined above. The downstream `completionStatus`
  // toast, `reviewStatus`, and the `reviewPolling` effect continue to consume
  // these from their original locations further down.
  const serverReviewStatus = chatData?.chat?.reviewStatus ?? null;

  // For project chats, the overall review is still pending until findingsSummary
  // arrives — serverReviewStatus is cleared per-ticket so it oscillates to null
  // between parallel ticket reviews. The killer-flow demo backs its `review_started`
  // phase with the typewriter window, so treat that as pending too so the
  // "Review complete" transition toast fires when the reveal finishes.
  const projectReviewPending =
    isPostReviewSummaryPending(localMessages) ||
    (!!activeProjectData &&
      (activeProjectData.ticketIds?.length ?? 0) > 0 &&
      (!activeProjectData.findingsSummary || isKillerFlowStreaming));

  const isReviewing = !!serverReviewStatus || projectReviewPending;

  const isProjectChat = chatData?.chat?.workflowType === "project";

  // Stall detection for `serverReviewStatus`. If the status holds a single
  // non-null value past the threshold we treat the review as effectively done.
  // This is the safety net that prevents a leaked/stuck `reviewStatus`
  // (documented bug class — see the reviewPolling effect below) from hiding the
  // right pane forever during a first review.
  const stallStartRef = useRef<{ status: string; t: number } | null>(null);
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!serverReviewStatus) {
      stallStartRef.current = null;
      setStalled(false);
      return;
    }
    // The post-review summary agent legitimately holds a single static
    // status string ("Generating review summary") for up to 5 min, so it is
    // exempt from the 120 s stall safety net (see the reviewPolling effect
    // and ask_puntt_workflow.py:159 for the rationale).
    if (serverReviewStatus === "Generating review summary") {
      stallStartRef.current = null;
      setStalled(false);
      return;
    }
    const prev = stallStartRef.current;
    if (!prev || prev.status !== serverReviewStatus) {
      stallStartRef.current = { status: serverReviewStatus, t: Date.now() };
      setStalled(false);
      return;
    }
    const REVIEW_POLL_STALL_MS = 120_000;
    const elapsed = Date.now() - prev.t;
    if (elapsed >= REVIEW_POLL_STALL_MS) {
      setStalled(true);
      return;
    }
    const timer = setTimeout(
      () => setStalled(true),
      REVIEW_POLL_STALL_MS - elapsed,
    );
    return () => clearTimeout(timer);
  }, [serverReviewStatus]);

  // An assistant reply landing after the most recent user turn is the visible
  // signal that the review actually finished. Memoized so both the gate below
  // and the `reviewPolling` effect read one source of truth.
  const assistantSinceUser = useMemo(() => {
    let lastUserIdx = -1;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      if (localMessages[i].role === "user") {
        lastUserIdx = i;
        break;
      }
    }
    return (
      lastUserIdx >= 0 &&
      localMessages
        .slice(lastUserIdx + 1)
        .some((m) => m.role === "assistant" && m.status !== "pending")
    );
  }, [localMessages]);

  const reviewStalled = stalled && assistantSinceUser;

  const hasActiveProjectStage =
    activeProjectData?.stages?.some((s) => s.status === "active") ?? false;

  /** UI-only: ticket findings can land before post-review summary / reviewStatus clears. */
  const reviewUiInProgress =
    projectReviewPending ||
    (!allTicketsReviewComplete &&
      ((!!serverReviewStatus && !reviewStalled) ||
        isKillerFlowStreaming ||
        hasActiveProjectStage));

  // Review limbo for UI (checklist + "Review complete" toast). Same
  // `isReviewUiInLimbo` predicate as `refetchInterval` above so long
  // single-ticket reviews suppress the false complete flash AND keep
  // polling until Key Findings / the post-review summary land.
  const viewTicketCreatedAt =
    getLatestViewTicketAssistantCreatedAt(localMessages);
  const viewTicketAssistantAgeMs = viewTicketCreatedAt
    ? Math.max(0, Date.now() - new Date(viewTicketCreatedAt).getTime())
    : null;
  const reviewInLimbo = isReviewUiInLimbo({
    hasViewTicketIdOnLastAssistant:
      hasViewTicketIdOnLastAssistant(localMessages),
    serverReviewStatus,
    viewTicketAssistantAgeMs,
    isMultiTicketReview: isMultiReview,
  });

  // Composer state: Stop while anything of this turn is still running on the
  // backend (agent turn OR review workflow), Send otherwise.
  const showStopButton = shouldShowStopButton({
    waitingForAI,
    reviewPolling,
    reviewInLimbo,
    stopRequested,
  });

  // Release the Stop latch once the server no longer reports a review — the
  // next review must get its own Stop affordance.
  useEffect(() => {
    if (!reviewPolling && !reviewInLimbo) setStopRequested(false);
  }, [reviewPolling, reviewInLimbo]);

  // Suppress the entire right pane (chat-only, full-width) while a brand-new
  // single-asset chat runs its FIRST review. Project / multi-asset / killer-
  // flow chats are excluded — they keep canvas + project info visible. See
  // shouldHideRightPaneForFirstReview for the full scope contract.
  const firstReviewInProgress = shouldHideRightPaneForFirstReview({
    isProjectChat,
    hasActiveProjectData: !!activeProjectData,
    isProjectLoading,
    isKillerFlowStreaming,
    // A chat that already produced a canvas isn't brand-new — keep its right
    // pane so the canvas stays visible during a review (canvas-vs-ticket
    // precedence applies instead of chat-only suppression).
    hasCanvasContent,
    isReviewing,
    reviewInLimbo,
    reviewStalled,
    // First review == the chat has produced at most one review ticket. We
    // count tickets (not "any viewTicketId") because viewTicketId is stamped
    // before reviewStatus turns on — see isFirstReviewForChat.
    isFirstReview: isFirstReviewForChat(allReviewTicketIds),
  });

  // Fallback `projectData` synthesized for the LEFT pane only.
  //
  // Ronnie's demo flow requires every in-flight review chat (single,
  // multi, custom) to look identical in the chat pane: yellow "AI
  // review in progress" banner above the composer + parallel
  // `ReviewChecklist` with one row per asset per check. The flows
  // each have a different way of signalling "a review is running"
  // and only one of them emits `projectData`:
  //
  //   * Multi-asset killer-flow      → backend emits `projectData`
  //   * Single-asset killer-flow     → backend emits `projectData`
  //                                    (post-unification, commit 68ff878bd)
  //                                    BUT christo deploys lag the FE,
  //                                    so we also accept
  //                                    `isKillerFlowStreaming` as an
  //                                    in-flight signal and synthesize
  //                                    from chat-level / message-level
  //                                    ticket ids.
  //   * Real-flow review (rembrandt) → no `projectData` ever; the
  //                                    workflow sets `chat.reviewStatus`
  //                                    instead. We synthesize from
  //                                    that signal + ticket ids.
  //
  // The stub only drives the CHECKLIST. `activeProjectData`
  // stays backend-only so the right-pane decision tree (which has
  // different requirements per flow — image preview for single-asset,
  // status cards for multi-asset) is unaffected by synthesis.
  //
  // Once the in-flight signal clears — `reviewStatus` cleared by the
  // workflow OR the typewriter finishes — this returns null and the
  // synthesized checklist items clear.
  const chatLevelTicketIds = (
    chatData?.chat as { ticketIds?: string[] } | undefined
  )?.ticketIds;
  const chatLevelReviewStatus = chatData?.chat?.reviewStatus;

  const leftPaneProjectData = useMemo((): AskPunttProjectData | null => {
    if (activeProjectData) return activeProjectData;
    const inFlight = !!chatLevelReviewStatus || isKillerFlowStreaming;
    if (!inFlight) return null;
    // Collect every ticket id we have any signal for, in priority
    // order: chat-level (set on persistence by the killer-flow path)
    // → assistant-message viewTicketId (set when a review completes)
    // → create-asset search-progress events (set the moment the
    // ticket exists, before the review finishes).
    const ids = new Set<string>();
    for (const id of chatLevelTicketIds ?? []) ids.add(id);
    for (const msg of localMessages) {
      if (msg.role === "assistant" && msg.viewTicketId) {
        ids.add(msg.viewTicketId);
      }
      for (const sp of msg.searchProgress ?? []) {
        if (isCreateAssetProgressSource(sp.source)) {
          for (const r of (sp.results ?? []) as {
            id?: string;
            title?: string;
          }[]) {
            if (r.id) ids.add(r.id);
          }
        }
      }
    }
    if (ids.size === 0) return null;
    // Single "active" stage keeps synthesis aligned with in-flight review
    // state for the floating checklist.
    return {
      ticketIds: Array.from(ids),
      stages: [{ name: "AI review", status: "active", color: "amber" }],
    } as AskPunttProjectData;
  }, [
    activeProjectData,
    chatLevelReviewStatus,
    chatLevelTicketIds,
    isKillerFlowStreaming,
    localMessages,
  ]);

  // For project chats: pool the canonical review checks from each ticket so
  // the ReviewChecklist rotates through items across all parallel reviews.
  // Items are interleaved (round-robin) across tickets so reveals feel
  // parallel rather than ticket-by-ticket. Driven by `leftPaneProjectData`
  // (backend-emitted OR synthesized stub) so single-asset and custom-asset
  // reviews show the same per-ticket rows as multi-asset killer-flow.
  const projectChecklistItems = useMemo(() => {
    if (!leftPaneProjectData) return undefined;
    if (chatData?.chat?.reviewChecklist) return undefined;
    const ticketIds = leftPaneProjectData.ticketIds ?? [];
    if (ticketIds.length === 0) return undefined;

    const titleMap = new Map<string, string>();
    for (const msg of localMessages) {
      for (const sp of msg.searchProgress ?? []) {
        if (isCreateAssetProgressSource(sp.source)) {
          for (const r of (sp.results ?? []) as {
            id?: string;
            title?: string;
          }[]) {
            if (r.id && r.title) titleMap.set(r.id, r.title);
          }
        }
      }
    }

    const checks = TICKET_REVIEW_CHECKS;
    const interleaved: { label: string; sub: string; ticketLabel: string }[] =
      [];
    for (let checkIdx = 0; checkIdx < checks.length; checkIdx++) {
      for (const ticketId of ticketIds) {
        const ticketLabel = titleMap.get(ticketId) ?? "Asset";
        interleaved.push({
          ticketLabel,
          label: checks[checkIdx].label,
          sub: checks[checkIdx].sub,
        });
      }
    }
    return interleaved;
  }, [leftPaneProjectData, localMessages, chatData?.chat?.reviewChecklist]);

  // Parallel-mode threshold is `>= 1` (not `> 1`) so single-asset and
  // custom-asset reviews share the multi-asset look. The checklist's
  // header line gracefully handles count=1 ("Reviewing your asset")
  // and count>1 ("Reviewing N assets in parallel").
  const projectTicketCount = leftPaneProjectData?.ticketIds?.length ?? 0;
  const projectParallelTicketCount =
    projectTicketCount >= 1 ? projectTicketCount : 0;

  // Track which ticket the user drilled into from the project view
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  const resetPaneCollapseState = useCallback(() => {
    setReviewChatCollapsed(false);
    setChatCollapsed(false);
    setArtworkCollapsed(false);
    setLabelSheetCollapsed(false);
  }, []);

  const backToProjectOverview = useCallback(() => {
    setSelectedTicketId(null);
    setActiveReviewTicketId(null);
    resetPaneCollapseState();
    clearChatReviewUrl();
  }, [resetPaneCollapseState, clearChatReviewUrl]);

  const closeStandaloneReview = useCallback(() => {
    setActiveReviewTicketId(null);
    artifactWorkspace.closeArtifact();
    resetPaneCollapseState();
    clearChatReviewUrl();
  }, [resetPaneCollapseState, clearChatReviewUrl, artifactWorkspace]);

  const handleCoworkOpenArtifact = useCallback(
    (
      payload: Parameters<typeof artifactWorkspace.openArtifact>[0],
      source: "sidebar" | "message_chip" = "sidebar",
    ) => {
      if (suppressRightPane) {
        if (payload.kind === "review" && onProjectReviewOpen) {
          onProjectReviewOpen(payload.ticketId);
        } else if (payload.kind === "canvas" && onProjectCanvasOpen) {
          onProjectCanvasOpen(payload.messageId);
        }
        return;
      }
      if (payload.kind === "review") {
        setTicketDismissed(false);
        if (activeProjectData) {
          setSelectedTicketId(payload.ticketId);
        }
        const idx = allReviewTicketIds.indexOf(payload.ticketId);
        if (idx >= 0) setSelectedReviewIndex(idx);
        navigateChatReview(payload.ticketId, null, { replace: false });
      } else if (payload.kind === "canvas") {
        setCanvasDismissed(false);
        navigateChatCanvas(payload.messageId, { replace: false });
      }
      artifactWorkspace.openArtifact(payload, source);
    },
    [
      suppressRightPane,
      onProjectReviewOpen,
      onProjectCanvasOpen,
      activeProjectData,
      artifactWorkspace,
      allReviewTicketIds,
      navigateChatReview,
      navigateChatCanvas,
    ],
  );

  const handleCoworkCloseArtifact = useCallback(() => {
    artifactWorkspace.closeArtifact();
    if (activeProjectData) {
      setSelectedTicketId(null);
    }
    setActiveReviewTicketId(null);
    setCanvasDismissed(true);
    setTicketDismissed(true);
    clearChatReviewUrl();
  }, [artifactWorkspace, activeProjectData, clearChatReviewUrl]);

  // When the user drills into a project ticket, use that ID for data fetching.
  // For project chats, only show a ticket when explicitly selected (drill-in);
  // otherwise the project overview is the default landing view.
  const effectiveViewTicketId = activeProjectData
    ? useCoworkArtifactMode
      ? (artifactWorkspace.activeViewTicketId ?? selectedTicketId)
      : selectedTicketId
    : coworkActiveViewTicketId;

  const artifactContextValue = useMemo<AskPunttArtifactContextValue>(
    () => ({
      activeTicketId: useCoworkArtifactMode
        ? coworkActiveViewTicketId
        : activeViewTicketId,
      activeCanvasMessageId,
      onActivateTicket,
      onActivateCanvas,
      isAnotherArtifactStreaming,
      allReviewTicketIds,
      canvasMessageIds,
      artifacts: useCoworkArtifactMode
        ? artifactWorkspace.artifacts
        : undefined,
      selection: useCoworkArtifactMode
        ? artifactWorkspace.selection
        : undefined,
      openArtifact: useCoworkArtifactMode
        ? handleCoworkOpenArtifact
        : undefined,
      closeArtifact: useCoworkArtifactMode
        ? handleCoworkCloseArtifact
        : undefined,
    }),
    [
      useCoworkArtifactMode,
      coworkActiveViewTicketId,
      activeViewTicketId,
      activeCanvasMessageId,
      onActivateTicket,
      onActivateCanvas,
      isAnotherArtifactStreaming,
      allReviewTicketIds,
      canvasMessageIds,
      artifactWorkspace.artifacts,
      artifactWorkspace.selection,
      handleCoworkOpenArtifact,
      handleCoworkCloseArtifact,
    ],
  );

  // Lift ticket data to parent to determine layout mode
  const {
    comments: viewComments,
    isLoading: viewTicketLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(effectiveViewTicketId, {
    pollingEnabled: reviewPolling,
  });

  // Progressive reveal contract for the Vitalora killer-flow demo:
  // the right-hand `ProjectStatusPane` now mounts immediately when
  // `activeProjectData` lands so the buyer sees ticket cards from
  // t=0 (with a per-card "Reviewing…" badge). Each card flips to
  // its finished state — severity badge, risk chip, comment count —
  // when the killer-flow typewriter finishes. The composer banner
  // stays hidden during streaming so the "needs human review" call-out
  // doesn't appear before reviewers have visibly run.
  const displayProjectData = activeProjectData;

  // Ticket ids for the browse rail: sequential reviews expose multiple
  // viewTicketIds on messages, but killer-flow multi-asset chats often
  // surface only one viewTicketId while `projectData.ticketIds` (and
  // `chat.ticketIds`) carry the full asset list.
  const multiTicketRailIds = useMemo(() => {
    if (allReviewTicketIds.length > 1) return allReviewTicketIds;
    const projectIds =
      displayProjectData?.ticketIds ?? chatData?.chat?.ticketIds ?? [];
    if (displayProjectData != null && projectIds.length > 1) {
      return projectIds;
    }
    return allReviewTicketIds;
  }, [allReviewTicketIds, displayProjectData, chatData?.chat?.ticketIds]);

  const coworkReviewArtifacts = useMemo(() => {
    if (artifactWorkspace.reviewArtifacts.length > 0) {
      return artifactWorkspace.reviewArtifacts;
    }
    return multiTicketRailIds.map((ticketId, index) => {
      const summary = reviewSummariesMap.get(ticketId);
      return {
        id: ticketId,
        kind: "review" as const,
        messageId: ticketId,
        title: reviewTicketTitleMap.get(ticketId) ?? `Review ${index + 1}`,
        ticketId,
        thumbnailUrl: summary?.thumbnailUrl ?? null,
        versionNumber: summary?.versionNumber,
        aiCommentCount: summary?.aiCommentCount,
        humanCommentCount: summary?.humanCommentCount,
      };
    });
  }, [
    artifactWorkspace.reviewArtifacts,
    multiTicketRailIds,
    reviewTicketTitleMap,
    reviewSummariesMap,
  ]);

  // Cowork-style artifact sidebar: persistent rail for >=1 artifact, click-to-open viewer.
  // Shown as soon as a review/document is created (including while generating);
  // firstReviewInProgress only suppresses auto-opening the review viewer, not the rail.
  const hasArtifactRail =
    useCoworkArtifactMode &&
    (artifactWorkspace.artifacts.length >= 1 || multiTicketRailIds.length > 1);

  const isCoworkBrowseMode =
    hasArtifactRail &&
    !artifactWorkspace.hasSelection &&
    selectedTicketId == null &&
    activeReviewTicketId == null;

  /** Viewer open from the artifact rail — sidebar hidden; standard 2/3-pane layouts. */
  const isCoworkArtifactViewMode =
    hasArtifactRail && artifactWorkspace.hasSelection;

  // Multi-ticket browse: fixed-width gray rail with Reviews + Chat Details
  // dropdowns (mirrors the project workspace). Applies to killer-flow chats
  // with `projectData` and standalone multi-review chats alike.
  const isMultiTicketBrowseMode =
    !hasArtifactRail &&
    multiTicketRailIds.length > 1 &&
    !suppressRightPane &&
    chatData?.chat?.workflowType !== "project" &&
    selectedTicketId == null &&
    activeReviewTicketId == null &&
    (!!displayProjectData || isStandaloneMultiReviewWorkspace);

  const chatUploadedFiles = useMemo(
    () => collectChatUploadAttachments(localMessages),
    [localMessages],
  );

  const chatUsedConnectors = useMemo(
    () => collectUsedConnectors(localMessages),
    [localMessages],
  );

  const reviewUsedConnectors = useMemo(
    () =>
      effectiveViewTicketId
        ? collectUsedConnectors(localMessages, {
            ticketId: effectiveViewTicketId,
          })
        : [],
    [localMessages, effectiveViewTicketId],
  );

  const standaloneReviewUsedConnectors = useMemo(
    () =>
      activeReviewTicketId
        ? collectUsedConnectors(localMessages, {
            ticketId: activeReviewTicketId,
          })
        : [],
    [localMessages, activeReviewTicketId],
  );

  const multiTicketChatDetails = useMemo(() => {
    const pd = displayProjectData;
    const workflowType = chatData?.chat?.workflowType ?? "";
    const hasActiveStage =
      pd?.stages?.some((s) => s.status === "active") ?? false;
    const reviewInProgress =
      !allTicketsReviewComplete &&
      (isKillerFlowStreaming ||
        !!chatData?.chat?.reviewStatus ||
        hasActiveStage);
    const findingsSummary = pd?.findingsSummary;
    return {
      title: chatData?.chat?.title ?? pd?.name ?? "Chat",
      workflowType,
      description: pd?.description ?? "",
      findingsSummary,
      ticketFindings: pd?.ticketFindings,
      isKillerFlowStreaming,
      hasActiveStage,
      reviewsStatus: reviewInProgress
        ? "IN REVIEW"
        : findingsSummary
          ? "COMPLETED"
          : undefined,
    };
  }, [
    displayProjectData,
    chatData?.chat?.title,
    chatData?.chat?.workflowType,
    chatData?.chat?.reviewStatus,
    isKillerFlowStreaming,
    allTicketsReviewComplete,
  ]);

  const isAgentResponding = holdAutoScroll;

  useEffect(() => {
    if (
      waitingForAI ||
      canvasPolling ||
      serverHasPendingMessage ||
      isKillerFlowStreaming ||
      isCanvasLoading
    ) {
      setHoldAutoScroll(true);
    }
  }, [
    waitingForAI,
    canvasPolling,
    serverHasPendingMessage,
    isKillerFlowStreaming,
    isCanvasLoading,
  ]);

  const {
    scrollContainerRef,
    sentinelRef: messagesEndRef,
    contentRef: messagesContentRef,
    handleScroll: handleMessagesScroll,
    resetScroll: resetAutoScroll,
    setAutoScrollPaused,
  } = useAutoScroll(
    [localMessages],
    // resetKey — switching to a new chat clears the sticky-up state so
    // the layout effect re-pins to the bottom of the new chat instead
    // of inheriting the previous chat's scroll position.
    chatId,
    { enabled: !isAgentResponding },
  );

  // Narrow feed chat columns (e.g. review 3-pane ~280px) drop the per-message
  // avatar column and use Slack-style consecutive-author headers instead.
  const feedChatColumnWidth = useContainerWidth(
    scrollContainerRef,
    messagePresentation === "feed",
  );
  const feedCompactAuthors =
    messagePresentation === "feed" &&
    feedChatColumnWidth != null &&
    feedChatColumnWidth <= FEED_COMPACT_AUTHOR_MAX_WIDTH_PX;

  const { reportPause, resetPauseSources } =
    useAutoScrollPauseRegistry(setAutoScrollPaused);

  // Clear viewport-intersection pause sources when switching chats.
  useEffect(() => {
    resetPauseSources();
  }, [chatId, resetPauseSources]);

  const handleChatMessagesScroll = useCallback(() => {
    if (handleMessagesScroll()) {
      setHoldAutoScroll(false);
    }
  }, [handleMessagesScroll]);

  // Explicit opens must unlock the viewer even while firstReviewInProgress
  // would otherwise force chat-only browse: shareable /reviews/:ticketId URLs,
  // and cowork rail clicks (artifact selection). Auto-open stays suppressed
  // when there is no URL deep link and no selection. Embedded/renderChat chats
  // do not sync review params, so selection is the only escape hatch there.
  const hasExplicitReviewOpen =
    Boolean(looseParams.ticketId ?? urlSearch.review) ||
    (useCoworkArtifactMode && artifactWorkspace.hasSelection);
  const blockViewerForFirstReview =
    firstReviewInProgress && !hasExplicitReviewOpen;

  // Enable 3-pane (chat + artwork + comments[/context]) when viewing a ticket.
  // Comments always live in the right column; context/reference assets share
  // that column when present. In project chats this fires only when the user
  // has drilled into a specific ticket (effectiveViewTicketId falls back to
  // selectedTicketId when activeProjectData is set, see above), so the
  // project overview remains the default landing view.
  // The right pane is suppressed when EITHER (a) the chat is embedded in the
  // project workspace (`suppressRightPane` prop), or (b) a brand-new
  // single-asset chat is running its first review (`firstReviewInProgress`,
  // see above) so the review viewer does not auto-open — unless the URL
  // already names a review. The cowork artifact rail still lists in-flight
  // reviews/documents in browse mode.
  const use3PaneLayout =
    !suppressRightPane &&
    !blockViewerForFirstReview &&
    !isCoworkBrowseMode &&
    (useCoworkArtifactMode
      ? !!effectiveViewTicketId
      : !isStandaloneMultiReviewWorkspace && !!effectiveViewTicketId) &&
    !activeCanvas;

  const contextPaneCollapsedLabel = hasContextAssets ? (
    <>
      {activePaneLabels?.context ?? "Context"} &middot; {contextFiles.length}{" "}
      {contextFiles.length === 1 ? "file" : "files"}
    </>
  ) : (
    (activePaneLabels?.context ?? "Comments")
  );

  const has2PaneRight =
    !suppressRightPane &&
    !blockViewerForFirstReview &&
    !isCoworkBrowseMode &&
    !isMultiTicketBrowseMode &&
    !use3PaneLayout &&
    !!(
      effectiveViewTicketId ||
      activeCanvas ||
      isProjectLoading ||
      activeProjectData
    );

  // Reset selected ticket when navigating to a different chat
  useEffect(() => {
    setSelectedTicketId(null);
    setActiveReviewTicketId(null);
    setHoldAutoScroll(false);
    resetPaneCollapseState();
  }, [chatId, resetPaneCollapseState]);

  // Hydrate review selection from URL on the standalone chat route.
  useEffect(() => {
    if (!isRouteDriven) return;
    const reviewId = looseParams.ticketId ?? urlSearch.review ?? null;
    if (!reviewId) {
      setSelectedTicketId(null);
      setActiveReviewTicketId(null);
      setTicketDismissed(false);
      return;
    }
    // User closed the viewer; do not re-open from a stale deep link while
    // clearChatReviewUrl navigates back to browse.
    if (ticketDismissed) return;
    const projectTicketIds = activeProjectData?.ticketIds ?? [];
    if (activeProjectData && projectTicketIds.includes(reviewId)) {
      setSelectedTicketId(reviewId);
      setTicketDismissed(false);
      return;
    }
    // Wait for history before treating the ticket as missing — otherwise we
    // would strip a valid deep link during the initial load flash.
    if (isLoading || !chatData) return;
    // Prefer the query payload for cold deep links (localMessages can still be
    // [] on the first post-load commit while chatData.messages already has
    // viewTicketIds). Also accept localMessages + active selection: streaming
    // stamps viewTicketId on localMessages first, and a rail click can open
    // /reviews/:ticketId before history has caught up — stripping then flashes
    // the viewer shut.
    const historyReviewTicketIds = collectReviewTicketIds(
      chatData.messages ?? [],
    );
    const reviewKnownInHistory = historyReviewTicketIds.includes(reviewId);
    const reviewKnownLocally = allReviewTicketIds.includes(reviewId);
    const reviewMatchesSelection =
      artifactWorkspace.activeViewTicketId === reviewId;
    if (
      !reviewKnownInHistory &&
      !reviewKnownLocally &&
      !reviewMatchesSelection
    ) {
      if (!chatId) return;
      navigate({
        ...buildChatPath(chatId),
        replace: true,
      });
      return;
    }

    // Multi-review legacy shell still uses activeReviewTicketId + chat-review-mode.
    openStandaloneReview(reviewId);

    // Cowork single-/multi-review chats mount the artifact viewer
    // (chat-artifact-view-mode) from selection — openStandaloneReview alone
    // does not open the rail viewer, so deep links stayed on browse mode.
    if (
      useCoworkArtifactMode &&
      artifactWorkspace.activeViewTicketId !== reviewId
    ) {
      artifactWorkspace.openArtifact(
        { kind: "review", ticketId: reviewId },
        "sidebar",
      );
    }
  }, [
    isRouteDriven,
    looseParams.ticketId,
    urlSearch.review,
    activeProjectData,
    chatData,
    openStandaloneReview,
    useCoworkArtifactMode,
    artifactWorkspace.activeViewTicketId,
    artifactWorkspace.openArtifact,
    allReviewTicketIds,
    ticketDismissed,
    isLoading,
    chatId,
    navigate,
  ]);

  // Hydrate canvas focus from path (or legacy search) on the standalone chat route.
  useEffect(() => {
    if (!isRouteDriven) return;
    const canvasId = looseParams.messageId ?? urlSearch.canvas;
    if (!canvasId) {
      setCanvasDismissed(false);
      return;
    }
    // User closed the viewer; do not re-open from a stale deep link while
    // clearChatReviewUrl navigates back to browse.
    if (canvasDismissed) return;
    setSelectedCanvasMessageId(canvasId);
    setCanvasDismissed(false);

    // Cowork mode displays canvas from artifactWorkspace selection — legacy
    // selectedCanvasMessageId alone leaves browse mode (same class of bug as
    // review deep links before openArtifact hydrate).
    if (
      useCoworkArtifactMode &&
      artifactWorkspace.activeCanvas?.messageId !== canvasId
    ) {
      artifactWorkspace.openArtifact(
        { kind: "canvas", messageId: canvasId },
        "sidebar",
      );
    }
  }, [
    isRouteDriven,
    looseParams.messageId,
    urlSearch.canvas,
    useCoworkArtifactMode,
    artifactWorkspace.activeCanvas?.messageId,
    artifactWorkspace.openArtifact,
    canvasDismissed,
  ]);

  // Auto-drill-in for single-ticket projects once the review reveal is
  // complete. Ronnie's flow: a single-asset Ask Puntt chat shows the
  // multi-asset right-pane look (`ProjectStatusPane` with one card and
  // a "Reviewing…" pill) WHILE the killer-flow typewriter is running,
  // then flips to `TicketViewPane` (image + pins + comments) the
  // moment the reveal finishes. Multi-ticket projects don't auto-
  // drill-in because the buyer needs the overview to scan all the
  // assets at once.
  //
  // Race-guard: when a fresh killer-flow response lands,
  // `activeProjectData` flips truthy on render N but
  // `useKillerFlowStream`'s `setActive(...)` doesn't flush its state
  // update until render N+1. Without `killerStreamingObservedRef` the
  // effect fires on render N seeing `isKillerFlowStreaming === false`
  // (stale), sets selectedTicketId, and the user lands on
  // `TicketViewPane` BEFORE the typewriter has had a chance to start.
  // The ref latches when streaming is first observed; when the latest
  // assistant message is a fresh killer-flow shape we hold off until
  // we've seen streaming actually fire (or until the message is older
  // than the staleness window, at which point the streaming hook
  // marked it handled and won't run — history-replay path).
  const killerStreamingObservedRef = useRef(false);
  useEffect(() => {
    if (isKillerFlowStreaming) {
      killerStreamingObservedRef.current = true;
    }
  }, [isKillerFlowStreaming]);

  useEffect(() => {
    if (useCoworkArtifactMode) return;
    if (selectedTicketId) return;
    if (!activeProjectData) return;
    const ticketIds = activeProjectData.ticketIds ?? [];
    if (ticketIds.length !== 1) return;
    if (isKillerFlowStreaming) return;
    const latest = localMessages[localMessages.length - 1];
    if (latest?.role === "assistant" && isKillerFlowAssistantMessage(latest)) {
      const createdAtMs = latest.createdAt ? Date.parse(latest.createdAt) : NaN;
      const isFresh =
        !Number.isNaN(createdAtMs) &&
        Date.now() - createdAtMs < DEFAULT_STALE_THRESHOLD_MS;
      if (isFresh && !killerStreamingObservedRef.current) return;
    }
    setSelectedTicketId(ticketIds[0]);
    if (isRouteDriven) {
      navigateChatReview(ticketIds[0], null, { replace: true });
    }
  }, [
    activeProjectData,
    isKillerFlowStreaming,
    selectedTicketId,
    localMessages,
    isRouteDriven,
    navigateChatReview,
  ]);

  const [labelSheetCollapsed, setLabelSheetCollapsed] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [artworkCollapsed, setArtworkCollapsed] = useState(false);

  const reviewColumnsData = useMemo(
    () => ({
      ticketId: effectiveViewTicketId ?? "",
      comments: viewComments,
      isLoading: viewTicketLoading,
      hasContextAssets,
      contextFiles,
      latestReviewFiles,
    }),
    [
      effectiveViewTicketId,
      viewComments,
      viewTicketLoading,
      hasContextAssets,
      contextFiles,
      latestReviewFiles,
    ],
  );

  const onCommentDeepLinkChange = useCallback(
    (commentId: string | null) => {
      if (!effectiveViewTicketId) return;
      navigateChatReview(effectiveViewTicketId, commentId, { replace: false });
    },
    [effectiveViewTicketId, navigateChatReview],
  );
  const onInvalidCommentDeepLink = useCallback(() => {
    if (!effectiveViewTicketId) return;
    navigateChatReview(effectiveViewTicketId, null, { replace: true });
  }, [effectiveViewTicketId, navigateChatReview]);

  const reviewColumns = useReviewArtworkContextColumns({
    ...reviewColumnsData,
    contextCollapsed: labelSheetCollapsed,
    onContextCollapsedChange: setLabelSheetCollapsed,
    initialCommentId: looseParams.commentId ?? null,
    onCommentDeepLinkChange,
    onInvalidCommentDeepLink,
  });

  const { handleCitationClick } = reviewColumns;

  // While the right pane is suppressed for a first review the chat is the only
  // thing on screen — make sure it can't be left in a collapsed
  // (opacity-0/pointer-events-none) state carried over from a prior chat,
  // which would blank the whole view with no expand affordance.
  useEffect(() => {
    if (firstReviewInProgress) setChatCollapsed(false);
  }, [firstReviewInProgress]);

  // When entering 3-column layout, collapse the app sidebar (if open) to free horizontal space
  const prev3Pane = useRef(false);
  useEffect(() => {
    if (use3PaneLayout && !prev3Pane.current && sidebarOpen) {
      dispatch(setDrawerOpen({ app: false }));
    }
    prev3Pane.current = use3PaneLayout;
  }, [use3PaneLayout, sidebarOpen, dispatch]);

  // Collapse the app nav sidebar when entering standalone multi-review mode.
  const prevStandaloneReviewRef = useRef(false);
  useEffect(() => {
    const inStandaloneReview =
      isStandaloneMultiReviewWorkspace && Boolean(activeReviewTicketId);
    if (inStandaloneReview && !prevStandaloneReviewRef.current && sidebarOpen) {
      dispatch(setDrawerOpen({ app: false }));
    }
    prevStandaloneReviewRef.current = inStandaloneReview;
  }, [
    isStandaloneMultiReviewWorkspace,
    activeReviewTicketId,
    sidebarOpen,
    dispatch,
  ]);

  const handleDismissCanvas = useCallback(() => {
    if (useCoworkArtifactMode) {
      handleCoworkCloseArtifact();
    } else {
      setCanvasDismissed(true);
    }
  }, [useCoworkArtifactMode, handleCoworkCloseArtifact]);

  const handleSectionClick = useCallback((sectionId: string, title: string) => {
    setSectionContext({ id: sectionId, title });
  }, []);

  const handleProjectDataUpdate = useCallback(
    (updatedData: AskPunttProjectData) => {
      queryClient.setQueryData(
        askPunttKeys.messages(chatId),
        (old: typeof chatData) => {
          if (!old?.chat) return old;
          return {
            ...old,
            chat: { ...old.chat, projectData: updatedData },
          };
        },
      );
    },
    [chatId, queryClient],
  );

  // The resize feature activates when the container is >= 768 px AND every
  // visible pane is uncollapsed. Narrower viewports and any collapsed state
  // fall back to the legacy flex layout so the existing 32 px expand bars +
  // 300 ms transitions keep working unchanged.
  const paneContainerRef = useRef<HTMLDivElement | null>(null);
  const containerWidth = useContainerWidth(paneContainerRef);
  const widthOk =
    containerWidth === null
      ? false
      : containerWidth >= RESIZE_LAYOUT_MIN_WIDTH_PX;
  const { prefs: uiPrefs, setPref: setUiPref } = useUiPrefs();
  // Artwork review-mode picker — mirrors the Ask Puntt home composer so the
  // Standard/Deep dropdown is also available from within a chat. The trigger
  // shows THIS chat's engine (`chat.reviewMode`, persisted by christo from the
  // create/send payload) so a chat started as Standard never reads "Deep
  // Review" just because the browser-wide pref was flipped in another chat.
  // Precedence: in-chat override for this session > the chat's persisted mode >
  // "standard". The browser-wide pref deliberately does NOT feed this — it only
  // seeds the home composer for brand-new chats; legacy chats that recorded no
  // mode ran the v1 engine, so Standard is the honest label for them.
  const [reviewModeOverride, setReviewModeOverride] =
    useState<ReviewMode | null>(null);
  const chatReviewMode = chatData?.chat?.reviewMode ?? null;
  const reviewMode: ReviewMode =
    reviewModeOverride ?? chatReviewMode ?? "standard";
  // Switching chats drops the override — the next chat shows its own mode.
  useEffect(() => {
    setReviewModeOverride(null);
  }, [chatId]);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const panelGroup2PaneRef = useRef<ImperativePanelGroupHandle>(null);
  const panelGroup3PaneRef = useRef<ImperativePanelGroupHandle>(null);

  // ---- Resize layout derivation ----
  // Resize is only "eligible" when every visible pane is expanded. Once any
  // pane collapses we fall back to the legacy flex layout so the existing
  // 32 px expand bars + collapse transitions keep working unchanged.
  const allPanesExpanded2 = !chatCollapsed && !artworkCollapsed;
  const allPanesExpanded3 =
    !chatCollapsed && !labelSheetCollapsed && !artworkCollapsed;
  const resizeBaseEligible = widthOk;
  const useResizable2Pane =
    resizeBaseEligible && has2PaneRight && allPanesExpanded2;
  const useResizable3Pane =
    resizeBaseEligible && use3PaneLayout && allPanesExpanded3;
  const useResizableLayout = useResizable2Pane || useResizable3Pane;

  // Storage key per visible right-pane variant so ticket/canvas/project
  // ratios don't bleed across each other. Ticket/canvas keys are versioned
  // so stale chat-heavy ratios from the old 50/50 default era reset once.
  const isTicketOrCanvas2Pane = !!(effectiveViewTicketId || activeCanvas);
  const twoPaneStorageKey = effectiveViewTicketId
    ? "askpuntt-2pane-chat-ticket-v3"
    : activeCanvas
      ? "askpuntt-2pane-chat-canvas-v3"
      : "askpuntt-2pane-chat-project";

  // Ticket/canvas 2-pane biases toward the review surface (right), matching
  // the project review workspace. Project overview keeps a balanced split.
  const twoPaneDefaultLayout = useMemo((): number[] => {
    if (isTicketOrCanvas2Pane) return [36, 64];
    return [50, 50];
  }, [isTicketOrCanvas2Pane]);

  // Mirror campaign review mins for ticket/canvas so the right pane cannot
  // shrink below 40%. Project overview keeps the looser balanced mins.
  const twoPaneMins: number[] = isTicketOrCanvas2Pane ? [18, 40] : [25, 30];
  const twoPaneChatMaxSize = isTicketOrCanvas2Pane ? 45 : undefined;
  // Chat | Artwork (center) | Context (right) — mirrors project review layout.
  const THREE_PANE_DEFAULT: number[] = [30, 46, 24];
  const THREE_PANE_MINS: number[] = [20, 30, 20];

  const twoPaneLayout = usePersistedLayout({
    storageKey: twoPaneStorageKey,
    defaultLayout: twoPaneDefaultLayout,
    minSizes: twoPaneMins,
  });

  // Reject chat-heavy persisted ratios for ticket/canvas (legacy 50/50 era or
  // manual drags). defaultSize only applies on PanelGroup mount, so clamp
  // before passing sizes into the panels.
  const effectiveTwoPaneLayout = useMemo((): number[] => {
    const raw = twoPaneLayout.layout;
    if (!isTicketOrCanvas2Pane) return raw;
    return clampTwoPaneLayout(raw, twoPaneDefaultLayout);
  }, [isTicketOrCanvas2Pane, twoPaneLayout.layout, twoPaneDefaultLayout]);

  const threePaneLayout = usePersistedLayout({
    storageKey: "askpuntt-3pane-chat-artwork-context-v2",
    defaultLayout: THREE_PANE_DEFAULT,
    minSizes: THREE_PANE_MINS,
  });

  // Abort signal for ResizeHandle — when chatId or effectiveViewTicketId
  // changes mid-drag, the handle synthesizes a pointerup so cursor +
  // userSelect body styles don't get stuck.
  const dragAbortSignal = `${chatId ?? ""}:${effectiveViewTicketId ?? ""}`;

  // Suppress the existing 300ms flex-grow transitions on the layout subtree
  // while a handle is being dragged. Without this the pane animations
  // fight the resize. Re-enabled the moment drag ends.
  const dragInProgress = activeDragId !== null;

  const questionFormRef = useRef<QuestionFormHandle>(null);
  const [_questionHasAnswer, setQuestionHasAnswer] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestionHasAnswer, setCurrentQuestionHasAnswer] =
    useState(false);
  const questionUploadInputRef = useRef<HTMLInputElement>(null);

  const handleStop = async () => {
    // pendingMessageId alone is not enough: it is only ever populated from the
    // streaming poll, which skips review-status messages. During an artwork
    // review it stays null, so Stop used to return here and the workflow kept
    // running. Fall back to the turn's own message so the cancel always lands.
    const msgId =
      pendingMessageId ??
      lastPendingIdRef.current ??
      findStopTargetMessageId(localMessages);
    // Optimistic: re-enable input immediately
    setWaitingForAI(false);
    setPendingMessageId(null);
    // Latch the review case too: cancelling the workflow does not clear
    // `chat.reviewStatus`, so `reviewPolling` alone would keep the button on
    // Stop until the stall detector fires ~2 min later.
    setStopRequested(true);

    if (!msgId) return;

    try {
      await abortMessage.mutateAsync(msgId);
    } catch {
      // Revert: restore polling so the message can complete naturally
      setWaitingForAI(true);
      setPendingMessageId(msgId);
      setStopRequested(false);
    }
  };

  const handleSend = useCallback(
    async (
      overrideMessage?: string,
      extraAttachments?: {
        key: string;
        source: string;
        type: "image" | "video" | "file";
        name?: string;
      }[],
    ) => {
      const text = overrideMessage ?? inputMessage;
      const allAttachments = [...readyAttachments, ...(extraAttachments ?? [])];
      if (
        (!text.trim() && allAttachments.length === 0) ||
        sendMessage.isPending ||
        hasPending
      )
        return;
      resetAutoScroll();
      resetPauseSources();
      setHoldAutoScroll(false);

      const messageToSend = text;
      const attachments = allAttachments;
      const messageLength = messageToSend.length;
      // localMessages changes on every streaming frame; read via ref to avoid
      // adding it to the dep array (which would recreate handleSend on every
      // rAF tick and defeat memo(ChatMessage)).
      const messageIndex = localMessagesRef.current.filter(
        (m) => m.role === "user",
      ).length;
      // Mentions only apply to the feed composer; the QuestionForm override path
      // (overrideMessage) carries no mentions.
      const mentionsToSend =
        overrideMessage === undefined && mentions.length > 0
          ? mentions
          : undefined;
      const mentionEmailsToSend =
        overrideMessage === undefined && mentionEmails.length > 0
          ? mentionEmails
          : undefined;

      const userMsg: AskPunttMessage = {
        _id: `local-user-${Date.now()}`,
        chatId,
        role: "user",
        content: messageToSend,
        userId: currentUserId,
        attachments: attachments.length > 0 ? attachments : undefined,
        mentions: mentionsToSend,
        mentionEmails: mentionEmailsToSend,
        createdAt: new Date().toISOString(),
      };

      const contextToSend = sectionContext;

      setLocalMessages((prev) => [...prev, userMsg]);
      setInputMessage("");
      setMentions([]);
      setMentionEmails([]);
      setSectionContext(null);
      clearAll();

      // Instrumentation: mark send time for TTFR measurement
      messageSentAtRef.current = Date.now();
      ttfrFiredRef.current = false;

      try {
        const result = await sendMessage.mutateAsync({
          message: messageToSend,
          attachments: attachments.length > 0 ? attachments : undefined,
          sectionContext: contextToSend ?? undefined,
          mentions: mentionsToSend,
          mentionEmails: mentionEmailsToSend,
          // Always send the engine choice: the backend routes to v2 only on an
          // explicit "deep", so an omitted value would mean v1 anyway — sending
          // it keeps the request self-describing and the routing logs honest.
          reviewMode,
        });

        messageCountRef.current += 1;

        // Replace the optimistic user message with the server-confirmed one
        setLocalMessages((prev) => {
          const withoutOptimistic = prev.filter((m) => m._id !== userMsg._id);
          return [
            ...withoutOptimistic,
            preserveUserMessageMentions(result.userMessage, userMsg),
          ];
        });
        // dataUpdatedAt read via ref — changes on every refetch and would
        // cause handleSend to be recreated on every poll tick if in deps.
        dataAtWaitStartRef.current = dataUpdatedAtRef.current;
        if (result.assistantMessage?._id) {
          setPendingMessageId(result.assistantMessage._id);
          lastPendingIdRef.current = result.assistantMessage._id;
        } else {
          lastPendingIdRef.current = null;
        }
        setWaitingForAI(true);

        const attachmentIds = attachments.map((att) => att.key);
        // chatData read via ref for the same reason as dataUpdatedAt.
        const chatConnectors =
          chatDataRef.current?.chat?.enabledConnectors ?? [];

        analytics.capture("ask_puntt_message_sent", {
          chat_id: chatId,
          message_length: messageLength,
          has_attachments: attachments.length > 0,
          attachment_count: attachments.length,
          attachment_ids: attachmentIds,
          enabled_connectors: chatConnectors,
          message_index: messageIndex,
          workflow_id: result.workflowId,
        });
        // The engagement insight counts ask_puntt_chat_created per user message,
        // so every send emits it too (is_new_chat: false marks follow-ups —
        // home/campaign chat creation fires it with is_new_chat absent/true).
        analytics.capture("ask_puntt_chat_created", {
          chat_id: chatId,
          message_length: messageLength,
          has_attachments: attachments.length > 0,
          attachment_count: attachments.length,
          attachment_ids: attachmentIds,
          enabled_connectors: chatConnectors,
          message_index: messageIndex,
          workflow_id: result.workflowId,
          is_new_chat: false,
        });
      } catch (error) {
        setLocalMessages((prev) => prev.filter((m) => m._id !== userMsg._id));

        const attachmentIds = attachments.map((att) => att.key);
        const chatConnectors =
          chatDataRef.current?.chat?.enabledConnectors ?? [];

        analytics.capture("ask_puntt_message_send_error", {
          chat_id: chatId,
          has_attachments: attachments.length > 0,
          attachment_count: attachments.length,
          attachment_ids: attachmentIds,
          enabled_connectors: chatConnectors,
          ...errorAnalyticsPayload(error),
        });
        console.error("Failed to send message:", error);
      }
    },
    [
      inputMessage,
      readyAttachments,
      hasPending,
      sendMessage,
      mentions,
      mentionEmails,
      sectionContext,
      clearAll,
      resetAutoScroll,
      resetPauseSources,
      chatId,
      analytics,
      currentUserId,
      reviewMode,
    ],
  );

  const chat = chatData?.chat;
  // Shared project feed only: stacked approval cards above the composer.
  // Personal chats and standalone routes never mount this accessory.
  const approvalProjectId = projectIdProp ?? chat?.projectId ?? null;
  const showApprovalRequestForm =
    messagePresentation === "feed" && !!approvalProjectId;
  // Owner-only affordances (Share button, Delete, etc.) — keyed off the
  // chat's `userId` field. `chat.role` is set by christo to "owner" or
  // "shared" but we double-check by comparing ids so the FE keeps working
  // even if the role field is ever omitted.
  const isChatOwner =
    !!chat &&
    !!currentUserId &&
    chat.userId === currentUserId &&
    chat.role !== "shared";
  const allowListMembers = chat?.allowList ?? [];
  const mentionComposerMembers = useMemo(() => {
    const seen = new Set<string>();
    const out: {
      _id: string;
      name?: string;
      email?: string;
      avatar?: string | null;
    }[] = [];
    const push = (m: {
      _id: string;
      name?: string;
      email?: string;
      avatar?: string | null;
    }) => {
      if (seen.has(m._id)) return;
      seen.add(m._id);
      out.push(m);
    };
    for (const m of feedMembers ?? []) push(m);
    for (const m of allowListMembers) push(m);
    return out;
  }, [allowListMembers, feedMembers]);

  // `serverReviewStatus`, `projectReviewPending`, and `isReviewing` are
  // computed above (hoisted alongside the first-review right-pane gate). The
  // "Review complete" transition toast below consumes `isReviewing` from
  // there — for project chats it stays true until findingsSummary arrives, so
  // the toast fires once at the end rather than per-ticket.
  const [completionStatus, setCompletionStatus] = useState<string | null>(null);
  const wasReviewingRef = useRef(false);
  useEffect(() => {
    const wasReviewing = wasReviewingRef.current;
    wasReviewingRef.current = isReviewing;
    if (
      shouldShowReviewCompleteTransition({
        wasReviewing,
        isReviewing,
        reviewInLimbo,
      })
    ) {
      setCompletionStatus("Review complete");
      const timer = setTimeout(() => setCompletionStatus(null), 5000);
      return () => clearTimeout(timer);
    }
    if (isReviewing) {
      setCompletionStatus(null);
    }
  }, [isReviewing, reviewInLimbo]);

  const reviewStatus = serverReviewStatus ?? completionStatus;

  // Start of the current review turn, for the in-chat review card's elapsed
  // clock. The latest user message is what kicked the review off, and its
  // timestamp is server-sourced, so a reload mid-review keeps counting from the
  // real start instead of restarting at 0:00.
  const latestUserMessageAt = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      if (localMessages[i].role === "user") {
        return localMessages[i].createdAt ?? null;
      }
    }
    return null;
  }, [localMessages]);

  // Sync review polling state with reviewStatus from server.
  //
  // Frontend safety-net for the "stuck reviewStatus" class of bug. Rembrandt's
  // post-review summary workflow used to leak `reviewStatus` on partial
  // failure paths (placeholder/context/agent errors would early-return
  // without clearing the field), leaving the ReviewChecklist banner stuck on
  // "Review in progress · Results on asset" even after every per-phase
  // checkmark went green and the assistant's summary message landed. The
  // root cause is fixed in `_run_post_review_summary` (try/finally so the
  // clear runs unconditionally), but historical stuck chats need a way out
  // and we don't want a future regression of the same shape to hang the UI
  // again.
  //
  // Stall-detector: if `serverReviewStatus` has held a single non-null
  // value for more than `REVIEW_POLL_STALL_MS` AND we've already received
  // an assistant message after the latest user turn, treat the review as
  // effectively done and stop polling locally. Anything *actively*
  // progressing on the backend re-stamps `reviewStatus` with a new phase
  // string and resets the stall timer, so this can never short-circuit a
  // genuinely in-flight review.
  // The stall detector (`stalled`) and `assistantSinceUser` are computed above
  // (hoisted for the first-review right-pane gate). This effect keeps
  // `reviewPolling` in sync: treat a stalled reviewStatus as "not polling" once
  // an assistant message has arrived after the most recent user turn — the
  // visible signal that the review actually finished. `reviewStalled` already
  // encodes `stalled && assistantSinceUser`.
  useEffect(() => {
    const effectiveReviewActive =
      (!!serverReviewStatus && !reviewStalled) || projectReviewPending;
    setReviewPolling(effectiveReviewActive);
  }, [serverReviewStatus, projectReviewPending, reviewStalled]);

  // When review polling stops, force a final fetch to pick up any comments
  // saved in the window between the last poll and polling stopping. Invalidate
  // EVERY review ticket in the chat — not only the open viewer ticket — so
  // browse-mode sidebar / thread cards refresh AI comment counts too. Also
  // re-invalidate the chat so the frontend catches a new reviewStatus that
  // the backend may have set for a post-review summary (the gap between the
  // review clearing reviewStatus and the summary setting it is ~50ms; the
  // invalidation fires a refetch that catches any new status in that window).
  const prevReviewPollingRef = useRef(false);
  useEffect(() => {
    if (prevReviewPollingRef.current && !reviewPolling) {
      for (const ticketId of allReviewTicketIds) {
        queryClient.invalidateQueries({
          queryKey: ["ticket-view", ticketId],
        });
        queryClient.invalidateQueries({
          queryKey: ["ticket-view-comments", ticketId],
        });
      }
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
    }
    prevReviewPollingRef.current = reviewPolling;
  }, [reviewPolling, allReviewTicketIds, queryClient, chatId]);

  // Find the active skill label from the last assistant message
  const activeSkillLabel = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.activeSkill) {
        return ASK_PUNTT_SKILL_LABELS[msg.activeSkill];
      }
    }
    return undefined;
  }, [localMessages]);

  // Extract suggested responses from the last assistant message
  const activeSuggestedResponses = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.suggestedResponses &&
      lastMsg.suggestedResponses.length > 0
    ) {
      return lastMsg.suggestedResponses;
    }
    return null;
  }, [localMessages]);

  // Hide suggestion pills when the last message has userQuestions (the form handles it)
  const lastMessage = localMessages[localMessages.length - 1];
  const hasQuestionForm =
    lastMessage?.role === "assistant" &&
    lastMessage.userQuestions &&
    lastMessage.userQuestions.length > 0;

  // Reset answer state when a new question form appears
  useEffect(() => {
    if (hasQuestionForm) {
      setQuestionHasAnswer(false);
      setCurrentQuestionHasAnswer(false);
      setCurrentQuestionIndex(0);
    }
  }, [hasQuestionForm]);

  // Auto-focus the chat input when the component mounts or when switching from question form to regular input
  useEffect(() => {
    if (!hasQuestionForm && !waitingForAI) {
      // Small delay to ensure the input is fully rendered
      const timer = setTimeout(() => {
        // Find the textarea directly by testid - Radix UI's TextArea
        // component renders as a textarea element itself, not a wrapper
        const textarea = document.querySelector(
          '[data-testid="ask-puntt-chat-reply-input"]',
        ) as HTMLTextAreaElement | null;

        if (textarea) {
          textarea.focus();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [hasQuestionForm, waitingForAI, chatId]);

  const totalQuestions = hasQuestionForm
    ? (lastMessage?.userQuestions?.length ?? 0)
    : 0;

  const handleQuestionNext = useCallback(() => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setCurrentQuestionHasAnswer(false);
    } else {
      questionFormRef.current?.submit();
    }
  }, [currentQuestionIndex, totalQuestions]);

  const handleQuestionBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  }, [currentQuestionIndex]);

  const handleUploadRequest = useCallback(() => {
    questionUploadInputRef.current?.click();
  }, []);
  const showSuggestions =
    activeSuggestedResponses &&
    !waitingForAI &&
    !sendMessage.isPending &&
    !hasQuestionForm;

  // When the review is complete, find the last review-status message so the
  // ReviewChecklist can be anchored inline at that position rather than always
  // floating to the bottom as new messages arrive.
  //
  // Latch: `hasObservedServerReviewStatusRef` flips to true the first time we
  // see a non-null `chat.reviewStatus` from the server and stays true for the
  // rest of the component's lifetime. We gate on `serverReviewStatus` (the
  // authoritative backend signal) rather than the local `reviewPolling` state
  // because `reviewPolling` is briefly flipped true by `setReviewPolling(true)`
  // at line ~406 whenever a fresh assistant message with a `viewTicketId`
  // lands — an FE artifact that does not mean the backend review pipeline is
  // actually running. If we latched on that flash we would wrongly conclude
  // the review had run, return `reviewChecklistComplete = true` during the
  // limbo window, and hide the floating checklist (the original regression).
  //
  // Combined with the `hasReviewStatusMessagesInHistory` fallback for
  // page-reload-after-completion, the latch correctly distinguishes "review
  // genuinely finished" from "review hasn't started yet."
  const hasObservedServerReviewStatusRef = useRef(false);
  useEffect(() => {
    if (serverReviewStatus) hasObservedServerReviewStatusRef.current = true;
  }, [serverReviewStatus]);
  const hasReviewStatusMessagesInHistory = useMemo(
    () => localMessages.some(isReviewStatusMessage),
    [localMessages],
  );
  const reviewChecklistComplete = deriveReviewChecklistComplete({
    hasReviewChecklist: !!chatData?.chat?.reviewChecklist,
    reviewPolling: reviewUiInProgress,
    hasObservedServerReviewStatus: hasObservedServerReviewStatusRef.current,
    hasReviewStatusMessagesInHistory,
    reviewInLimbo,
  });
  const reviewChecklistAnchorIdx = reviewChecklistComplete
    ? localMessages.reduce(
        (last, m, i) => (isReviewStatusMessage(m) ? i : last),
        -1,
      )
    : -1;
  const hasUserFollowUpAfterReview = useMemo(() => {
    const lastReviewIdx = findLastReviewEvidenceIdx(localMessages);
    return hasUserMessageAfterReview(localMessages, lastReviewIdx);
  }, [localMessages]);
  // A post-review summary can only be pending if a review actually ran.
  // `isPostReviewSummaryPending` only knows "the last message is an empty or
  // pending assistant turn" — which is equally true of the placeholder written
  // while the agent is still calling create_ticket, minutes BEFORE any review
  // starts. Unqualified, it flipped the checklist to "Review complete" while
  // the ticket was still being created (and stayed there when create_ticket
  // failed outright, so a dead run read as a finished one).
  const reviewHasRun =
    hasObservedServerReviewStatusRef.current ||
    hasReviewStatusMessagesInHistory;
  const reviewChecklistUiComplete =
    reviewChecklistComplete ||
    serverReviewStatus === "Generating review summary" ||
    (reviewHasRun && isPostReviewSummaryPending(localMessages));
  const postReviewSummaryMessageIdx =
    findPostReviewSummaryMessageIdx(localMessages);
  const lastMsgForChecklist = localMessages[localMessages.length - 1];
  const lastIsAssistantForChecklist = lastMsgForChecklist?.role === "assistant";
  const lastAssistantIsPendingForChecklist =
    lastIsAssistantForChecklist &&
    !!lastMsgForChecklist &&
    (lastMsgForChecklist.status === "pending" || !lastMsgForChecklist.content);
  const hasReviewChecklistOnChat = !!chatData?.chat?.reviewChecklist;
  const hasCreateTicketInHistory = localMessages.some((m) =>
    m.agentSteps?.some(
      (s) => s.type === "tool_call" && s.tool === "create_ticket",
    ),
  );
  const showFloatingReviewChecklist = shouldShowFloatingReviewChecklist({
    waitingForAI,
    lastIsAssistant: lastIsAssistantForChecklist,
    lastAssistantIsPending: lastAssistantIsPendingForChecklist,
    reviewPolling,
    hasReviewChecklist: hasReviewChecklistOnChat,
    isKillerFlowStreaming,
    hasCreateTicket: hasCreateTicketInHistory,
    reviewChecklistComplete,
    reviewChecklistAnchorIdx,
    hasReviewStages: !!chatData?.chat?.reviewStages?.length,
    hasUserFollowUpAfterReview,
  });

  // The v2 stage bar also renders in the ARTWORK pane, but that pane is not
  // mounted while the user waits in the chat — which is exactly when progress
  // matters. So it takes over the same in-thread slot the legacy checklist used
  // (they are mutually exclusive: the predicate above returns false once
  // reviewStages exist, and only v2 ever writes that field).
  // Deliberately NOT gated on `reviewPolling`. The workflow clears
  // `reviewStatus` when the review ends, which drops reviewPolling — and gating
  // on it made the finished bar (stages, scouts, every finding) vanish the
  // moment the review completed, taking the record of the run with it. The
  // fields survive in the chat doc, and the activity's terminal event leaves
  // every stage done, so the bar can render its own finished state.
  const showFloatingReviewProgress = !!chatData?.chat?.reviewStages?.length;
  const showFloatingReviewSurface =
    showFloatingReviewChecklist || showFloatingReviewProgress;

  const showThreadArtifactCards =
    useCoworkArtifactMode &&
    !firstReviewInProgress &&
    (coworkReviewArtifacts.length > 0 ||
      artifactWorkspace.canvasArtifacts.length > 0);

  const threadArtifactsByAnchorMessageId = useMemo(() => {
    if (!showThreadArtifactCards) {
      return new Map<string, ChatArtifact[]>();
    }
    const allArtifacts = [
      ...coworkReviewArtifacts,
      ...artifactWorkspace.canvasArtifacts,
    ];
    return buildThreadArtifactsByAnchorMessageId(allArtifacts, localMessages);
  }, [
    showThreadArtifactCards,
    coworkReviewArtifacts,
    artifactWorkspace.canvasArtifacts,
    localMessages,
  ]);

  const renderThreadArtifactCardsForMessage = (
    messageId: string,
    artifacts: readonly ChatArtifact[],
    isStreaming: boolean,
  ) => {
    if (!showThreadArtifactCards || isStreaming) return null;
    const visible = artifacts.filter((a) => !a.isGenerating);
    if (visible.length === 0) return null;
    return (
      <ChatThreadArtifactCards
        artifacts={visible}
        anchorMessageId={messageId}
        isArtifactActive={artifactWorkspace.isArtifactActive}
        isArtifactUnread={isArtifactUnreadForViewer}
        onOpenArtifact={(payload) =>
          handleCoworkOpenArtifact(payload, "message_chip")
        }
      />
    );
  };

  // ---- Hoisted inner JSX for the chat / context / artwork / 2-pane right
  // panes. Each is referenced from BOTH the legacy flex layout AND the new
  // PanelGroup branches so we keep the source-of-truth in one place. The
  // wrappers (which differ between layouts) are inlined at the call sites.
  const chatPaneInner = (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden bg-puntt-neutral-gray-1"
      data-testid="ask-puntt-chat-pane"
    >
      {(use3PaneLayout || has2PaneRight) && !chatCollapsed && (
        <div
          className={`flex ${PANE_HEADER_HEIGHT_CLASS} ${PANE_HEADER_SURFACE_CLASS} ${PANE_HEADER_SIDE_INSET_CLASS} shrink-0 items-center justify-end border-b border-puntt-neutral-gray-6`}
          data-testid="chat-pane-header"
        >
          <Tooltip content="Collapse chat pane">
            <PaneHeaderIconButton
              aria-label="Collapse chat pane"
              data-testid="pane-collapse-chat"
              onClick={() => setChatCollapsed(true)}
            >
              <ArrowsInSimple size={PANE_HEADER_ICON_SIZE} aria-hidden="true" />
            </PaneHeaderIconButton>
          </Tooltip>
        </div>
      )}
      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleChatMessagesScroll}
        className="flex-1 overflow-y-auto px-4 py-6"
        data-testid="ask-puntt-messages"
      >
        <div
          ref={messagesContentRef}
          className="mx-auto w-full max-w-[720px] space-y-4"
        >
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4"
                />
              ))}
            </div>
          ) : (
            <AskPunttArtifactProvider value={artifactContextValue}>
              {(() => {
                let floatingChecklistSplicedInMessageItems = false;

                const buildFloatingReviewChecklistNode = () => {
                  // v2 drives a real, phase-accurate bar — show it instead of the
                  // legacy client-side-timer checklist. Same slot, so it inherits
                  // the splice-above-summary and bottom-widget placement.
                  if (showFloatingReviewProgress) {
                    return (
                      <ReviewProgressBar
                        stages={chatData?.chat?.reviewStages ?? []}
                        evidence={chatData?.chat?.reviewEvidence}
                        stream={chatData?.chat?.reviewStream}
                        warnings={chatData?.chat?.reviewWarnings}
                        complete={reviewChecklistUiComplete}
                        instanceId="thread"
                        startedAt={latestUserMessageAt}
                      />
                    );
                  }
                  const reviewStillActive =
                    (reviewPolling || reviewInLimbo) &&
                    !reviewChecklistUiComplete;
                  const isStreamingReveal = reviewStillActive;
                  return (
                    <ScrollPauseWhenVisible
                      scrollRootRef={scrollContainerRef}
                      enabled={isStreamingReveal}
                      pauseId="floating-review-checklist"
                      reportPause={reportPause}
                    >
                      <ReviewChecklist
                        workflowType={chatData?.chat?.workflowType ?? ""}
                        isReviewPolling={reviewStillActive}
                        reviewComplete={reviewChecklistUiComplete}
                        dynamicItems={
                          chatData?.chat?.reviewChecklist ??
                          projectChecklistItems
                        }
                        parallelTicketCount={
                          hasReviewChecklistOnChat
                            ? undefined
                            : projectParallelTicketCount || undefined
                        }
                        // Standard (v1) has no stage bar: the server phase +
                        // elapsed clock are the only live progress the chat
                        // column can show.
                        phase={serverReviewStatus}
                        startedAt={latestUserMessageAt}
                      />
                    </ScrollPauseWhenVisible>
                  );
                };

                // Build the per-message render items with ALL the existing
                // per-message logic intact (review-status hiding, killer-stream
                // typewriter, last-assistant flags). Each carries its createdAt
                // so project feed `timelineExtras` can be interleaved by time.
                const messageItems: {
                  key: string;
                  createdAt: string;
                  node: ReactNode;
                }[] = [];
                let prevVisibleFeedAuthorKey: string | null = null;
                localMessages.forEach((message, idx) => {
                  // Display-only messages (e.g. the synthesized transcript an
                  // ingested shared chat is stored as) are read by the agent
                  // but never rendered in the timeline.
                  if (isHiddenMessage(message)) {
                    return;
                  }
                  const isLast = idx === localMessages.length - 1;
                  const isLastAssistant =
                    message.role === "assistant" && isLast && !waitingForAI;
                  const precedingUserHasAttachments =
                    isLast &&
                    message.role === "assistant" &&
                    waitingForAI &&
                    !!localMessages[idx - 1]?.attachments?.length;

                  // Hide per-ticket "review underway" messages when the
                  // ReviewChecklist is active so the checklist is the
                  // single source of review progress. The post-map
                  // ReviewChecklist replaces the last one. Don't hide a
                  // mid-stream message that happens to match the shape.
                  const isReviewStatusMsg = isReviewStatusMessage(message);
                  const reviewChecklistActive =
                    reviewPolling || !!chatData?.chat?.reviewChecklist;
                  // Feed: skip the empty Puntt placeholder row; thinking UI
                  // renders below the thread until steps/content arrive.
                  if (
                    messagePresentation === "feed" &&
                    isEmptyAssistantPlaceholder(message)
                  ) {
                    return;
                  }

                  if (
                    isReviewStatusMsg &&
                    reviewChecklistActive &&
                    !hasUserFollowUpAfterReview &&
                    (!isLast || !waitingForAI)
                  ) {
                    // Inline anchor: once the review is complete, render the
                    // ReviewChecklist at the last review-status message's
                    // position so it stays fixed in the chat as new messages
                    // arrive below it.
                    if (idx === reviewChecklistAnchorIdx) {
                      messageItems.push({
                        key: message._id,
                        createdAt: message.createdAt ?? "",
                        node: (
                          <ReviewChecklist
                            workflowType={chatData?.chat?.workflowType ?? ""}
                            isReviewPolling={false}
                            reviewComplete={reviewChecklistUiComplete}
                            dynamicItems={
                              chatData?.chat?.reviewChecklist ??
                              projectChecklistItems
                            }
                            parallelTicketCount={undefined}
                          />
                        ),
                      });
                    }
                    return;
                  }

                  // Theatrical typewriter override for the killer flow:
                  // when this is the message currently being paced,
                  // swap its `contentBlocks` and slice its `agentSteps`
                  // so ChatMessage renders the partial reveal. Once
                  // streaming finishes, `revealedContentBlocks` is null
                  // and the message renders normally.
                  const isStreamingTarget =
                    killerStream.activeMessageId === message._id &&
                    killerStream.revealedContentBlocks !== null;
                  const displayedMessage = isStreamingTarget
                    ? {
                        ...message,
                        contentBlocks:
                          killerStream.revealedContentBlocks ?? undefined,
                        agentSteps:
                          killerStream.revealedAgentStepCount != null
                            ? (message.agentSteps ?? []).slice(
                                0,
                                killerStream.revealedAgentStepCount,
                              )
                            : message.agentSteps,
                      }
                    : message;

                  const feedActor =
                    messagePresentation === "feed" &&
                    displayedMessage.role === "user"
                      ? resolveFeedActorForUserMessage(displayedMessage, {
                          currentUserId,
                          chatOwnerId: chat?.userId,
                          members: feedMembers,
                          viewerName: feedActorName,
                          viewerAvatar: feedActorAvatar,
                        })
                      : null;

                  const feedAuthorKey =
                    messagePresentation === "feed"
                      ? feedMessageAuthorKey(displayedMessage)
                      : null;
                  const showFeedAuthorHeader =
                    feedAuthorKey == null ||
                    !feedCompactAuthors ||
                    isFeedAuthorGroupStart(
                      prevVisibleFeedAuthorKey,
                      feedAuthorKey,
                    );
                  if (feedAuthorKey != null) {
                    prevVisibleFeedAuthorKey = feedAuthorKey;
                  }

                  const isStreamingAssistant =
                    displayedMessage.role === "assistant" &&
                    isLast &&
                    ((waitingForAI && message.role === "assistant") ||
                      isStreamingTarget);

                  const chatMessageNode = (
                    <ChatMessage
                      message={displayedMessage}
                      chatId={chatId}
                      reviewFiles={latestReviewFiles}
                      localPreviewUrls={localPreviewUrls}
                      isStreaming={
                        (waitingForAI &&
                          isLast &&
                          message.role === "assistant") ||
                        isStreamingTarget
                      }
                      isLastAssistant={isLastAssistant && !isStreamingTarget}
                      onSubmitAnswers={handleSend}
                      formDisabled={sendMessage.isPending || waitingForAI}
                      questionFormRef={
                        isLast && hasQuestionForm ? questionFormRef : undefined
                      }
                      onAnswerChange={
                        isLast && hasQuestionForm
                          ? setQuestionHasAnswer
                          : undefined
                      }
                      hideInlineQuestionForm={!!(isLast && hasQuestionForm)}
                      precedingUserHasAttachments={precedingUserHasAttachments}
                      presentation={messagePresentation}
                      feedActorName={feedActor?.name ?? feedActorName}
                      feedActorAvatar={feedActor?.avatar ?? feedActorAvatar}
                      feedOwnUserMessage={
                        messagePresentation === "feed" &&
                        displayedMessage.role === "user" &&
                        isFeedMessageFromViewer(displayedMessage, {
                          currentUserId,
                          chatOwnerId: chat?.userId,
                        })
                      }
                      feedMembers={feedMembers}
                      collapsible={
                        messagePresentation === "feed" &&
                        message._id !== lastVisibleMessageId
                      }
                      feedCompactLayout={feedCompactAuthors}
                      showAuthorHeader={showFeedAuthorHeader}
                    />
                  );

                  const threadArtifactsForMessage =
                    threadArtifactsByAnchorMessageId.get(message._id) ?? [];

                  messageItems.push({
                    key: message._id,
                    createdAt: message.createdAt ?? "",
                    node: isStreamingAssistant ? (
                      <ScrollPauseWhenVisible
                        scrollRootRef={scrollContainerRef}
                        enabled={true}
                        pauseId="streaming-assistant-message"
                        reportPause={reportPause}
                      >
                        {chatMessageNode}
                      </ScrollPauseWhenVisible>
                    ) : (
                      <>
                        <div className="[contain-intrinsic-size:auto_120px] [content-visibility:auto]">
                          {chatMessageNode}
                        </div>
                        {renderThreadArtifactCardsForMessage(
                          message._id,
                          threadArtifactsForMessage,
                          false,
                        )}
                      </>
                    ),
                  });
                });

                // Walk a time-ordered list and emit a centered day-divider
                // before the first item of each new calendar day (Slack-style
                // date context). The first rendered day always gets a divider.
                // Dividers carry the item's createdAt so they interleave with
                // project feed events the same way messages do.
                const now = new Date();
                const renderWithDayDividers = (
                  items: { key: string; createdAt: string; node: ReactNode }[],
                ) => {
                  const out: ReactNode[] = [];
                  let prevDay: string | null = null;
                  for (const item of items) {
                    const day = calendarDayKey(item.createdAt);
                    // Only emit a divider once we have a parseable day that
                    // differs from the previous rendered day. Undated items
                    // never start a new day group (they ride under the prior
                    // divider) and never suppress the next dated divider.
                    // Compare the local-day KEYS (not re-parsed dates) so a
                    // UTC-midnight reparse can't shift the boundary.
                    if (day && day !== prevDay) {
                      out.push(
                        <DayDivider
                          key={`day-divider-${day}-${item.key}`}
                          label={formatDayDivider(item.createdAt, now)}
                          day={day}
                        />,
                      );
                      prevDay = day;
                    }
                    out.push(<Fragment key={item.key}>{item.node}</Fragment>);
                  }
                  return out;
                };

                // When a post-review summary exists, splice the floating
                // checklist above it so streaming summary text never renders
                // above the review-status toggle.
                if (
                  showFloatingReviewSurface &&
                  postReviewSummaryMessageIdx >= 0
                ) {
                  const summaryMsg = localMessages[postReviewSummaryMessageIdx];
                  const insertAt = messageItems.findIndex(
                    (item) => item.key === summaryMsg._id,
                  );
                  if (insertAt >= 0) {
                    messageItems.splice(insertAt, 0, {
                      key: "review-checklist-floating",
                      createdAt: summaryMsg.createdAt ?? "",
                      node: buildFloatingReviewChecklistNode(),
                    });
                    floatingChecklistSplicedInMessageItems = true;
                  }
                }

                const messagesRendered =
                  timelineExtras.length === 0
                    ? renderWithDayDividers(messageItems)
                    : (() => {
                        const merged = [
                          ...messageItems.map((item, order) => ({
                            ...item,
                            order,
                          })),
                          ...timelineExtras.map((extra, i) => ({
                            key: extra.id,
                            createdAt: extra.createdAt,
                            node: extra.node,
                            order: messageItems.length + i,
                          })),
                        ];
                        merged.sort((a, b) => {
                          const ta = Date.parse(a.createdAt) || 0;
                          const tb = Date.parse(b.createdAt) || 0;
                          if (ta !== tb) return ta - tb;
                          return a.order - b.order;
                        });
                        return renderWithDayDividers(merged);
                      })();

                const lastMsg = lastMsgForChecklist;
                const lastIsAssistant = lastIsAssistantForChecklist;

                let bottomWidget: ReactNode = null;
                if (
                  showFloatingReviewSurface &&
                  !floatingChecklistSplicedInMessageItems
                ) {
                  bottomWidget = buildFloatingReviewChecklistNode();
                } else {
                  const showFeedPlaceholderThinking =
                    messagePresentation === "feed" &&
                    waitingForAI &&
                    lastIsAssistant &&
                    lastMsg &&
                    isEmptyAssistantPlaceholder(lastMsg);

                  if (
                    waitingForAI &&
                    (!lastIsAssistant || showFeedPlaceholderThinking)
                  ) {
                    bottomWidget = (
                      <ScrollPauseWhenVisible
                        scrollRootRef={scrollContainerRef}
                        enabled={true}
                        pauseId="thread-thinking"
                        reportPause={reportPause}
                      >
                        <ThinkingState
                          message={
                            showFeedPlaceholderThinking ? lastMsg : undefined
                          }
                          precedingUserHasAttachments={
                            !!localMessages[localMessages.length - 1]
                              ?.attachments?.length
                          }
                        />
                      </ScrollPauseWhenVisible>
                    );
                  }
                }

                return (
                  <FeedCompactLayoutProvider value={feedCompactAuthors}>
                    {messagesRendered}
                    {bottomWidget}
                  </FeedCompactLayoutProvider>
                );
              })()}
            </AskPunttArtifactProvider>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area — approval cards sit above the composer, not inside it */}
      <div className="shrink-0 border-t border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1 p-4">
        <div className="mx-auto w-full max-w-[720px] space-y-3">
          {showApprovalRequestForm && approvalProjectId ? (
            <ApprovalRequestComposer
              projectId={approvalProjectId}
              currentUserId={currentUserId}
              onCardHighlight={onApprovalCardHighlight}
            />
          ) : null}

          <div
            className="relative overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20"
            data-testid="ask-puntt-chat-composer"
          >
            <input
              ref={questionUploadInputRef}
              data-testid="ask-puntt-file-upload-input"
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(Array.from(e.target.files));
                e.target.value = "";
              }}
            />

            {hasQuestionForm && !waitingForAI && (
              <div className="border-b border-puntt-neutral-gray-6 px-4 pb-2 pt-3">
                <Flex align="center" justify="between">
                  <Text size="1" className="text-puntt-neutral-gray-9">
                    Question {currentQuestionIndex + 1} of {totalQuestions}
                  </Text>
                  <Flex gap="1">
                    {Array.from({ length: totalQuestions }).map((_, i) => (
                      <div
                        key={i}
                        className={`size-1.5 rounded-full transition-colors duration-200 ${
                          i <= currentQuestionIndex
                            ? "bg-puntt-accent-9"
                            : "bg-puntt-neutral-gray-5"
                        }`}
                      />
                    ))}
                  </Flex>
                </Flex>
              </div>
            )}

            {showSuggestions && (
              <SuggestedResponses
                suggestions={activeSuggestedResponses}
                onSelect={(suggestion) => {
                  analytics.capture("ask_puntt_suggested_response_clicked", {
                    chat_id: chatId,
                    suggestion_text: suggestion.slice(0, 100),
                    workflow_type: chatData?.chat?.workflowType,
                  });
                  handleSend(suggestion);
                }}
                disabled={sendMessage.isPending || waitingForAI}
              />
            )}

            {/* Section context chip */}
            {sectionContext && (
              <div className="flex animate-chip-slide-down items-center gap-2 overflow-hidden border-b border-puntt-accent-4 bg-puntt-accent-2 px-3 py-1.5 text-xs text-puntt-accent-11">
                <span className="flex-1 truncate">
                  Re: <strong>{sectionContext.title}</strong>
                </span>
                <button
                  data-testid="chat-button-1472"
                  onClick={() => setSectionContext(null)}
                  aria-label="Remove section context"
                  className="shrink-0 cursor-pointer rounded p-0.5 hover:bg-puntt-accent-3 focus:outline-none focus:ring-1 focus:ring-puntt-accent-9"
                >
                  ×
                </button>
              </div>
            )}
            {hasQuestionForm && !waitingForAI ? (
              <div className="px-3 py-2">
                <QuestionForm
                  data-testid="chat-question-form-1483"
                  ref={questionFormRef}
                  questions={
                    (lastMessage?.userQuestions as AskPunttUserQuestion[]) ?? []
                  }
                  onSubmit={(formatted, attachments) => {
                    analytics.capture("ask_puntt_question_form_submitted", {
                      chat_id: chatId,
                      question_count: totalQuestions,
                      workflow_type: chatData?.chat?.workflowType,
                    });
                    handleSend(formatted || undefined, attachments);
                  }}
                  disabled={sendMessage.isPending || waitingForAI || hasPending}
                  hideSubmitButton
                  visibleQuestionIndex={currentQuestionIndex}
                  onCurrentQuestionAnswerChange={setCurrentQuestionHasAnswer}
                  onUploadRequest={handleUploadRequest}
                  chatLevelFileCount={attachedItems.length}
                />
              </div>
            ) : (
              <MentionTextarea
                data-testid={
                  messagePresentation === "feed"
                    ? "ask-puntt-feed-mention-composer"
                    : "ask-puntt-chat-mention-composer"
                }
                value={inputMessage}
                members={mentionComposerMembers}
                rosterMembers={feedMembers ?? []}
                enableDirectorySearch
                composerAriaLabel={
                  messagePresentation === "feed"
                    ? "Reply to the Shared Project Feed"
                    : "Reply to the chat"
                }
                placeholder="Reply..."
                onChange={(value, nextMentions, nextEmails) => {
                  setInputMessage(value);
                  setMentions(nextMentions);
                  setMentionEmails(nextEmails);
                }}
                onSend={() => handleSend()}
                onPaste={handlePaste}
              />
            )}

            {/* Attached files preview */}
            {attachedItems.length > 0 && (
              <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                {attachedItems.map((item, idx) => (
                  <Flex
                    key={item.id}
                    align="center"
                    gap="1"
                    className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                    data-testid="ask-puntt-attached-file"
                  >
                    {item.status === "pending" && (
                      <CircleNotch
                        size={12}
                        weight="bold"
                        className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                      />
                    )}
                    <Text
                      size="1"
                      className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                    >
                      {item.file.name}
                      {item.status === "error" && " (failed)"}
                    </Text>
                    <button
                      data-testid="chat-button-1554"
                      onClick={() => removeFile(idx)}
                      className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                      aria-label={`Remove ${item.file.name}`}
                    >
                      ×
                    </button>
                  </Flex>
                ))}
              </Flex>
            )}

            <Flex
              justify="between"
              align="end"
              className={hasQuestionForm && !waitingForAI ? "px-3 py-2" : "p-1"}
            >
              {hasQuestionForm && !waitingForAI ? (
                <>
                  <Button
                    data-testid="chat-button-1569"
                    size="2"
                    variant="soft"
                    color="gray"
                    disabled={sendMessage.isPending || waitingForAI}
                    onClick={() => questionFormRef.current?.submitAny()}
                    className="cursor-pointer rounded-xl"
                  >
                    Just Get Started
                    <ArrowRight size={14} weight="bold" />
                  </Button>
                  <Flex gap="2" align="center">
                    <Button
                      data-testid="chat-button-1581"
                      size="2"
                      variant="soft"
                      color="gray"
                      disabled={
                        currentQuestionIndex === 0 ||
                        sendMessage.isPending ||
                        waitingForAI
                      }
                      onClick={handleQuestionBack}
                      className="cursor-pointer rounded-xl"
                    >
                      <ArrowLeft size={14} weight="bold" />
                      Back
                    </Button>
                    <Button
                      data-testid="question-next-btn"
                      size="2"
                      variant="solid"
                      color="blue"
                      disabled={
                        !currentQuestionHasAnswer ||
                        sendMessage.isPending ||
                        waitingForAI ||
                        hasPending
                      }
                      onClick={handleQuestionNext}
                      className="cursor-pointer rounded-xl"
                    >
                      {currentQuestionIndex < totalQuestions - 1
                        ? "Next"
                        : "Submit Answers"}
                      <ArrowRight size={14} weight="bold" />
                    </Button>
                  </Flex>
                </>
              ) : (
                <>
                  <InputPlusMenu
                    onFilesSelected={addFiles}
                    onScreenshotCaptured={addFile}
                    enabledConnectors={connectorsArrayToRecord(
                      chat?.enabledConnectors,
                      availableConnectors,
                    )}
                    connectorsDisabled
                    availableConnectors={availableConnectors}
                  />
                  <Flex align="center" gap="2">
                    <ReviewModeControl
                      mode={reviewMode}
                      onModeChange={(m) => {
                        // Override wins for this chat until the send persists
                        // it server-side; the pref keeps seeding new chats.
                        setReviewModeOverride(m);
                        setUiPref("artworkReviewMode", m);
                      }}
                      // Locked while a review/turn is running — the engine
                      // can't be switched mid-review.
                      disabled={showStopButton}
                    />
                    {showStopButton ? (
                      <Button
                        data-testid="ask-puntt-chat-stop"
                        size="2"
                        variant="soft"
                        color="gray"
                        onClick={handleStop}
                        disabled={abortMessage.isPending}
                        className="cursor-pointer rounded-xl transition-all duration-200"
                        aria-label="Stop generating"
                      >
                        <Stop size={14} weight="fill" />
                        Stop
                      </Button>
                    ) : (
                      <IconButton
                        data-testid="ask-puntt-chat-send"
                        size="2"
                        variant="solid"
                        color="blue"
                        disabled={
                          (!inputMessage.trim() &&
                            readyAttachments.length === 0) ||
                          sendMessage.isPending ||
                          hasPending
                        }
                        onClick={() => handleSend()}
                        className="cursor-pointer rounded-xl transition-all duration-200"
                        aria-label="Send message"
                      >
                        {hasPending ? (
                          <CircleNotch
                            size={14}
                            weight="bold"
                            className="animate-spin"
                          />
                        ) : (
                          <ArrowUp size={14} weight="bold" />
                        )}
                      </IconButton>
                    )}
                  </Flex>
                </>
              )}
            </Flex>
          </div>
        </div>
      </div>
    </div>
  );

  const contextPaneInner3Pane = (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-base-white">
      <ReviewContextColumn
        data={reviewColumnsData}
        review={reviewColumns}
        contextCollapseTestId="pane-collapse-context"
        commentsEmptyTestId="chat-artwork-comments-empty"
        onPasswordRequired={handlePasswordRequired}
        onContextCollapse={() => setLabelSheetCollapsed(true)}
        usedConnectors={reviewUsedConnectors}
      />
    </div>
  );

  const artworkPaneInner3Pane = (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-base-white">
      <ReviewArtworkColumn
        data={reviewColumnsData}
        review={reviewColumns}
        artworkTitle={activePaneLabels?.artwork ?? "Artwork"}
        artworkBodyTestId="chat-artwork-body"
        artworkHeaderLeading={
          isCoworkArtifactViewMode &&
          artifactWorkspace.selection?.kind === "review" ? (
            <ArtifactPaneCloseButton
              onClick={handleCoworkCloseArtifact}
              data-testid="chat-artifact-viewer-close"
              ariaLabel="Close review"
            />
          ) : selectedTicketId && activeProjectData ? (
            <PaneHeaderIconButton
              onClick={backToProjectOverview}
              aria-label="Back to project"
              data-testid="back-to-project"
            >
              <ArrowLeft size={PANE_HEADER_ICON_SIZE} aria-hidden="true" />
            </PaneHeaderIconButton>
          ) : undefined
        }
        artworkHeaderTrailing={
          <>
            {reviewStatus ? (
              <Text
                size="1"
                className={`shrink-0 italic ${
                  reviewStatus === "Review complete"
                    ? "text-malachite-700"
                    : "animate-pulse text-puntt-accent-9"
                }`}
              >
                {reviewStatus}
              </Text>
            ) : null}
          </>
        }
        artworkCollapseControl={
          isCoworkArtifactViewMode ? undefined : (
            <Tooltip content="Collapse artwork pane">
              <PaneHeaderIconButton
                onClick={() => setArtworkCollapsed(true)}
                aria-label="Collapse artwork pane"
                data-testid="pane-collapse-artwork"
              >
                <ArrowsInSimple
                  size={PANE_HEADER_ICON_SIZE}
                  aria-hidden="true"
                />
              </PaneHeaderIconButton>
            </Tooltip>
          )
        }
        artworkBetweenHeaderAndBody={
          <>
            {/* The review progress bar lives in the thread only. Mounted here it
                sits between the pane header and the artwork and pushes the pack
                off screen — and once the run finishes the bar keeps its whole
                record open, so the pane the user opened to LOOK at the artwork
                shows a list of findings instead of the artwork. */}
            {isMultiReview &&
            !activeProjectData &&
            (useCoworkArtifactMode
              ? isCoworkArtifactViewMode
              : !isStandaloneMultiReviewWorkspace) ? (
              <ReviewNavStrip
                reviewIndex={selectedReviewIndex}
                totalReviews={allReviewTicketIds.length}
                onPrevReview={handlePrevReview}
                onNextReview={handleNextReview}
              />
            ) : null}
          </>
        }
        onPasswordRequired={handlePasswordRequired}
      />
    </div>
  );

  const rightPane2PaneInner =
    activeProjectData && selectedTicketId ? (
      <TicketViewPane
        ticketId={selectedTicketId}
        onCollapse={() => setArtworkCollapsed(true)}
        onBackToProject={
          // Single-ticket projects have no meaningful
          // "overview" to return to — one card on a bare
          // pane. Hiding the back affordance also keeps
          // the auto-drill-in effect from fighting a user
          // who accidentally hits Back (the effect would
          // immediately re-select the lone ticket and the
          // UI would jitter).
          (activeProjectData.ticketIds?.length ?? 0) > 1
            ? backToProjectOverview
            : undefined
        }
        reviewStatus={reviewStatus}
        onCitationClick={handleCitationClick}
        isKillerFlowStreaming={isKillerFlowStreaming}
      />
    ) : isProjectChat && isProjectLoading && !activeProjectData ? (
      <ProjectLoadingPane />
    ) : activeCanvas ? (
      <div className="relative h-full" data-testid="ask-puntt-canvas-area">
        <CanvasPane
          canvas={activeCanvas.canvas}
          onDismiss={handleDismissCanvas}
          skillName={
            activeCanvas.skill
              ? ASK_PUNTT_SKILL_LABELS[activeCanvas.skill]
              : undefined
          }
          chatId={chatId}
          isLoading={isCanvasLoading}
          onSectionClick={handleSectionClick}
          leadingClose={
            isCoworkArtifactViewMode
              ? { ariaLabel: "Close document" }
              : undefined
          }
        />
      </div>
    ) : displayProjectData ? (
      <ProjectStatusPane
        projectData={displayProjectData}
        activeTicketId={effectiveViewTicketId}
        ticketIds={chatData?.chat?.ticketIds ?? []}
        onTicketSelect={setSelectedTicketId}
        chatId={chatId}
        chatCreatedAt={chatData?.chat?.createdAt}
        onSendMessage={(msg) => handleSend(msg)}
        onProjectDataUpdate={handleProjectDataUpdate}
        actionsDisabled={sendMessage.isPending || waitingForAI}
        isLoading={isProjectLoading}
        isKillerFlowStreaming={isKillerFlowStreaming}
      />
    ) : effectiveViewTicketId && !use3PaneLayout ? (
      <TicketViewPane
        ticketId={effectiveViewTicketId}
        onCollapse={() => setArtworkCollapsed(true)}
        reviewStatus={reviewStatus}
        onCitationClick={handleCitationClick}
        reviewIndex={selectedReviewIndex}
        totalReviews={allReviewTicketIds.length}
        onPrevReview={handlePrevReview}
        onNextReview={handleNextReview}
        isKillerFlowStreaming={isKillerFlowStreaming}
      />
    ) : null;

  return (
    <AskPunttDropZone
      onFilesDropped={addFiles}
      className="relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
      data-testid="ask-puntt-chat"
    >
      {passwordPdfViewerWarning && (
        <div
          className="absolute right-4 top-4 z-[70] max-w-sm rounded-lg border border-puntt-gold-7 bg-base-white p-4 shadow-lg"
          data-testid="password-pdf-warning-toast"
          aria-label="Password-protected PDF warning"
          role="status"
        >
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
            Password protected PDF
          </Text>
          <Text as="p" size="1" className="mt-1 text-puntt-neutral-gray-11">
            {passwordPdfViewerWarning} is encrypted. Download it and open with
            your PDF viewer to enter the password.
          </Text>
          <Flex justify="end" mt="2">
            <Button
              data-testid="password-pdf-warning-dismiss-button"
              size="1"
              variant="soft"
              aria-label="Dismiss password warning"
              onClick={() => setPasswordPdfViewerWarning(null)}
              className="cursor-pointer"
            >
              Dismiss
            </Button>
          </Flex>
        </div>
      )}
      {/* Breadcrumb — hidden when embedded in the project workspace (it
          supplies its own chat header / privacy banner). */}
      {!isEmbeddedInProjectWorkspace && (
        <PageHeader>
          <BreadcrumbHeaderTrail className="min-w-0 flex-1 overflow-hidden">
            <BreadcrumbHeaderRoot
              to={askPunttRoute.to}
              label="Ask Puntt"
              testId="ask-puntt-breadcrumb-root"
              showSeparator={!!chat?.title}
            />
            {chat?.title && (
              <ChatBreadcrumbTitle
                chatId={chat._id}
                title={chat.title}
                canEdit={isChatOwner}
              />
            )}
            {chat?.ownerName && (
              <Badge
                size="1"
                variant="soft"
                color="gray"
                className={BREADCRUMB_HEADER_CHIP_CLASS}
                data-testid="ask-puntt-chat-owner-badge"
              >
                owned by {chat.ownerName}
              </Badge>
            )}
            {activeSkillLabel && (
              <Badge
                size="1"
                variant="soft"
                color="blue"
                className={BREADCRUMB_HEADER_CHIP_CLASS}
              >
                {activeSkillLabel}
              </Badge>
            )}
          </BreadcrumbHeaderTrail>
          {isChatOwner &&
            !isEmbeddedInProjectWorkspace &&
            !isProjectChat &&
            chat && (
              <span className="ml-auto flex items-center gap-2">
                <PromoteToProjectButton chatId={chat._id} />
                <Button
                  data-testid="ask-puntt-share-chat-button"
                  aria-label="Share chat"
                  size="1"
                  variant="soft"
                  color="gray"
                  className="cursor-pointer"
                  onClick={() => setShareDialogOpen(true)}
                >
                  <ShareNetwork size={14} aria-hidden="true" />
                  <Text size="1">Share</Text>
                </Button>
              </span>
            )}
          {isChatOwner && !isEmbeddedInProjectWorkspace && isProjectChat && (
            <Button
              data-testid="ask-puntt-share-chat-button"
              aria-label="Share chat"
              size="1"
              variant="soft"
              color="gray"
              className="ml-auto cursor-pointer"
              onClick={() => setShareDialogOpen(true)}
            >
              <ShareNetwork size={14} aria-hidden="true" />
              <Text size="1">Share</Text>
            </Button>
          )}
          {ticketDismissed && hasViewTicketId && (
            <Button
              data-testid="chat-button-1170"
              size="1"
              variant="soft"
              color="blue"
              className={
                isChatOwner ? "cursor-pointer" : "ml-auto cursor-pointer"
              }
              onClick={() => setTicketDismissed(false)}
            >
              <ArrowsOutSimple size={14} />
              <Text size="1">Show Review</Text>
            </Button>
          )}
          {canvasDismissed && hasCanvasContent && (
            <Button
              data-testid="chat-button-1182"
              size="1"
              variant="soft"
              color="blue"
              className={`cursor-pointer ${!(ticketDismissed && hasViewTicketId) && !isChatOwner ? "ml-auto" : ""}`}
              onClick={() => setCanvasDismissed(false)}
            >
              <ArrowsOutSimple size={14} />
              <Text size="1">Show Canvas</Text>
            </Button>
          )}
        </PageHeader>
      )}

      {chat && (
        <ShareAskPunttChatDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          chatId={chat._id}
          chatTitle={chat.title}
          members={allowListMembers}
        />
      )}

      {/* Main content: cowork artifact sidebar, multi-pane, or legacy browse shells */}
      {isCoworkBrowseMode ? (
        <div
          className="relative flex min-h-0 w-full min-w-0 flex-1 overflow-hidden"
          data-testid="chat-browse-mode"
          aria-label="Chat workspace"
        >
          <div className="flex size-full min-h-0 min-w-0">
            <div
              className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-r border-puntt-neutral-gray-6"
              data-testid="pane-chat"
            >
              {chatPaneInner}
            </div>
            <ChatWorkspaceRail
              reviewArtifacts={coworkReviewArtifacts}
              canvasArtifacts={artifactWorkspace.canvasArtifacts}
              isArtifactActive={artifactWorkspace.isArtifactActive}
              onOpenArtifact={handleCoworkOpenArtifact}
              isArtifactUnread={isArtifactUnreadForViewer}
              title={multiTicketChatDetails.title}
              workflowType={multiTicketChatDetails.workflowType}
              description={multiTicketChatDetails.description}
              findingsSummary={multiTicketChatDetails.findingsSummary}
              ticketFindings={multiTicketChatDetails.ticketFindings}
              uploadedFiles={chatUploadedFiles}
              usedConnectors={chatUsedConnectors}
              reviewsStatus={multiTicketChatDetails.reviewsStatus}
              isKillerFlowStreaming={
                multiTicketChatDetails.isKillerFlowStreaming
              }
              hasActiveStage={multiTicketChatDetails.hasActiveStage}
              detailsOpen={chatDetailsSectionOpen}
              onDetailsOpenChange={setChatDetailsSectionOpen}
            />
          </div>
        </div>
      ) : isStandaloneMultiReviewWorkspace &&
        activeReviewTicketId &&
        !hasArtifactRail ? (
        <div
          className="relative flex min-h-0 w-full min-w-0 flex-1 overflow-hidden"
          data-testid="chat-review-mode"
          aria-label="Chat review workspace"
        >
          <div className="flex size-full min-h-0 min-w-0">
            {reviewChatCollapsed ? (
              <button
                type="button"
                onClick={() => setReviewChatCollapsed(false)}
                aria-label="Expand chat"
                aria-expanded={false}
                data-testid="chat-review-chat-expand"
                className="flex w-8 shrink-0 flex-col items-center gap-2 border-r border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1 py-3 text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
              >
                <CaretRight size={14} aria-hidden="true" />
                <span className="text-[11px] font-bold uppercase tracking-wide [writing-mode:vertical-rl]">
                  Chat
                </span>
              </button>
            ) : (
              <div
                className="flex shrink-0 flex-col border-r border-puntt-neutral-gray-5"
                style={{ width: `min(${REVIEW_CHAT_FALLBACK_PX}px, 30vw)` }}
              >
                <div
                  className={`flex ${PANE_HEADER_HEIGHT_CLASS} ${PANE_HEADER_SURFACE_CLASS} ${PANE_HEADER_SIDE_INSET_CLASS} shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-6`}
                >
                  <h2 className="flex-1 truncate text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11">
                    {chat?.title ?? "Chat"}
                  </h2>
                  <Tooltip content="Collapse chat">
                    <PaneHeaderIconButton
                      onClick={() => setReviewChatCollapsed(true)}
                      aria-label="Collapse chat"
                      data-testid="chat-review-chat-collapse"
                    >
                      <ArrowsInSimple
                        size={PANE_HEADER_ICON_SIZE}
                        aria-hidden="true"
                      />
                    </PaneHeaderIconButton>
                  </Tooltip>
                </div>
                <div
                  className="flex min-h-0 flex-1 flex-col overflow-hidden"
                  data-testid="pane-chat"
                >
                  {chatPaneInner}
                </div>
              </div>
            )}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <ReviewNavStrip
                reviewIndex={selectedReviewIndex}
                totalReviews={allReviewTicketIds.length}
                onPrevReview={handlePrevReview}
                onNextReview={handleNextReview}
              />
              <div className="min-h-0 flex-1 overflow-hidden">
                <CampaignReviewPane
                  ticketId={activeReviewTicketId}
                  onClose={closeStandaloneReview}
                  initialCommentId={looseParams.commentId ?? null}
                  onCommentDeepLinkChange={(commentId) => {
                    navigateChatReview(activeReviewTicketId, commentId, {
                      replace: false,
                    });
                  }}
                  onInvalidCommentDeepLink={() => {
                    navigateChatReview(activeReviewTicketId, null, {
                      replace: true,
                    });
                  }}
                  usedConnectors={standaloneReviewUsedConnectors}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          ref={paneContainerRef}
          className="relative flex min-h-0 w-full min-w-0 flex-1 overflow-hidden"
          data-testid={
            isCoworkArtifactViewMode ? "chat-artifact-view-mode" : undefined
          }
        >
          {useResizable3Pane ? (
            <PanelGroup
              key="askpuntt-3pane-chat-artwork-context-v2"
              direction="horizontal"
              ref={panelGroup3PaneRef}
              onLayout={threePaneLayout.onLayout}
              // Intentionally no autoSaveId: persistence is owned by
              // usePersistedLayout (250 ms debounce + validateLayout clamp).
              // Setting autoSaveId in addition would let the library bypass
              // the clamp on subsequent visits and double-write to a parallel
              // localStorage key.
              className={`flex flex-1 ${dragInProgress ? "[&_*]:!transition-none" : ""}`}
            >
              <Panel
                id="chat"
                order={0}
                minSize={THREE_PANE_MINS[0]}
                defaultSize={threePaneLayout.layout[0]}
              >
                <div
                  className="relative flex h-full min-w-0 flex-col overflow-hidden"
                  data-testid="pane-chat"
                >
                  {chatPaneInner}
                </div>
              </Panel>
              <ResizeHandle
                id="chat-right"
                panelGroupRef={panelGroup3PaneRef}
                defaultLayout={THREE_PANE_DEFAULT}
                abortSignal={dragAbortSignal}
                onDraggingChange={(d) =>
                  setActiveDragId(d ? "chat-right" : null)
                }
              />
              <Panel
                id="artwork"
                order={1}
                minSize={THREE_PANE_MINS[1]}
                defaultSize={threePaneLayout.layout[1]}
              >
                <div
                  className="h-full animate-canvas-slide-in"
                  data-testid="pane-artwork"
                >
                  {artworkPaneInner3Pane}
                </div>
              </Panel>
              <ResizeHandle
                id="context-artwork"
                panelGroupRef={panelGroup3PaneRef}
                defaultLayout={THREE_PANE_DEFAULT}
                abortSignal={dragAbortSignal}
                onDraggingChange={(d) =>
                  setActiveDragId(d ? "context-artwork" : null)
                }
              />
              <Panel
                id="context"
                order={2}
                minSize={THREE_PANE_MINS[2]}
                defaultSize={threePaneLayout.layout[2]}
              >
                <div
                  className="h-full animate-canvas-slide-in"
                  data-testid="pane-context"
                >
                  {contextPaneInner3Pane}
                </div>
              </Panel>
            </PanelGroup>
          ) : useResizable2Pane ? (
            <PanelGroup
              key={`askpuntt-2pane-${twoPaneStorageKey}`}
              direction="horizontal"
              ref={panelGroup2PaneRef}
              onLayout={twoPaneLayout.onLayout}
              // See note on the 3-pane PanelGroup above: persistence is owned
              // by usePersistedLayout, not the library's autoSaveId.
              className={`flex flex-1 ${dragInProgress ? "[&_*]:!transition-none" : ""}`}
            >
              <Panel
                id="chat"
                order={0}
                minSize={twoPaneMins[0]}
                maxSize={twoPaneChatMaxSize}
                defaultSize={effectiveTwoPaneLayout[0]}
              >
                <div
                  className="relative flex h-full min-w-0 flex-col overflow-hidden"
                  data-testid="pane-chat"
                >
                  {chatPaneInner}
                </div>
              </Panel>
              <ResizeHandle
                id="chat-right"
                panelGroupRef={panelGroup2PaneRef}
                defaultLayout={twoPaneDefaultLayout}
                abortSignal={dragAbortSignal}
                onDraggingChange={(d) =>
                  setActiveDragId(d ? "chat-right" : null)
                }
              />
              <Panel
                id="right"
                order={1}
                minSize={twoPaneMins[1]}
                defaultSize={effectiveTwoPaneLayout[1]}
              >
                <div
                  className="h-full animate-canvas-slide-in"
                  data-testid="pane-right"
                >
                  {rightPane2PaneInner}
                </div>
              </Panel>
            </PanelGroup>
          ) : (
            <>
              {/* Chat Pane — flex fallback uses a fixed chat column (or weighted
                  flex in 3-pane) so wide message content cannot steal width
                  from the review surface. */}
              {!chatCollapsed && (has2PaneRight || use3PaneLayout) ? (
                <div
                  className={`relative flex shrink-0 flex-col overflow-hidden border-r border-puntt-neutral-gray-6 ${
                    use3PaneLayout ? "min-h-0 min-w-0" : ""
                  }`}
                  style={
                    use3PaneLayout
                      ? {
                          flexGrow: THREE_PANE_DEFAULT[0],
                          flexShrink: 1,
                          flexBasis: "0%",
                          minWidth: 0,
                          transition:
                            "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
                        }
                      : {
                          width: `min(${REVIEW_CHAT_FALLBACK_PX}px, 30vw)`,
                        }
                  }
                  data-testid="pane-chat"
                >
                  {chatPaneInner}
                </div>
              ) : !(has2PaneRight || use3PaneLayout) ? (
                <div
                  className={
                    isEmbeddedInProjectWorkspace
                      ? "relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                      : "relative flex w-full flex-col overflow-hidden"
                  }
                >
                  {chatPaneInner}
                </div>
              ) : null}

              {/* Right Pane(s) */}
              {use3PaneLayout ? (
                <>
                  <CollapsedPaneLabel
                    collapsed={chatCollapsed}
                    onExpand={() => setChatCollapsed(false)}
                    ariaLabel="Expand Chat pane"
                    testId="pane-expand-chat"
                    labelRender={() => "Chat"}
                  />

                  <CollapsedPaneLabel
                    collapsed={artworkCollapsed}
                    onExpand={() => setArtworkCollapsed(false)}
                    ariaLabel="Expand Artwork pane"
                    testId="pane-expand-artwork"
                    labelRender={() => (
                      <>
                        {activePaneLabels?.artwork ?? "Artwork"} &middot;{" "}
                        {latestReviewFiles.length}{" "}
                        {latestReviewFiles.length === 1 ? "file" : "files"}
                      </>
                    )}
                  />

                  {/* Artwork pane (center) */}
                  <div
                    data-testid="pane-artwork"
                    style={{
                      flexGrow: artworkCollapsed ? 0 : THREE_PANE_DEFAULT[1],
                      flexShrink: 1,
                      flexBasis: "0%",
                      minWidth: 0,
                      overflow: "hidden",
                      transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
                    }}
                    className={`animate-canvas-slide-in ${
                      artworkCollapsed
                        ? ""
                        : "border-r border-puntt-neutral-gray-6"
                    }`}
                  >
                    <div
                      className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                        artworkCollapsed
                          ? "translate-x-5 opacity-0"
                          : "translate-x-0 opacity-100"
                      }`}
                    >
                      {artworkPaneInner3Pane}
                    </div>
                  </div>

                  <CollapsedPaneLabel
                    collapsed={labelSheetCollapsed}
                    onExpand={() => setLabelSheetCollapsed(false)}
                    ariaLabel={
                      hasContextAssets
                        ? "Expand Context pane"
                        : "Expand Comments pane"
                    }
                    testId="pane-expand-context"
                    labelRender={() => contextPaneCollapsedLabel}
                  />

                  {/* Context pane (right) */}
                  <div
                    data-testid="pane-context"
                    style={{
                      flexGrow: labelSheetCollapsed ? 0 : THREE_PANE_DEFAULT[2],
                      flexShrink: 1,
                      flexBasis: "0%",
                      minWidth: 0,
                      overflow: "hidden",
                      transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
                    }}
                    className="animate-canvas-slide-in"
                  >
                    <div
                      className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                        labelSheetCollapsed
                          ? "translate-x-5 opacity-0"
                          : "translate-x-0 opacity-100"
                      }`}
                    >
                      {contextPaneInner3Pane}
                    </div>
                  </div>
                </>
              ) : has2PaneRight ? (
                <>
                  <CollapsedPaneLabel
                    collapsed={chatCollapsed}
                    onExpand={() => setChatCollapsed(false)}
                    ariaLabel="Expand Chat pane"
                    testId="pane-expand-chat"
                    labelRender={() => "Chat"}
                  />

                  <CollapsedPaneLabel
                    collapsed={artworkCollapsed}
                    onExpand={() => setArtworkCollapsed(false)}
                    ariaLabel="Expand pane"
                    testId="pane-expand-artwork"
                    labelRender={() =>
                      effectiveViewTicketId
                        ? "Ticket"
                        : activeCanvas
                          ? "Canvas"
                          : "Details"
                    }
                  />

                  {/* Right pane content (2-pane) */}
                  <div
                    data-testid="pane-right"
                    style={{
                      flexGrow: artworkCollapsed ? 0 : 1,
                      flexShrink: 1,
                      flexBasis: "0%",
                      minWidth: 0,
                      overflow: "hidden",
                      transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
                    }}
                    className="min-h-0 min-w-0 flex-1 animate-canvas-slide-in"
                  >
                    <div
                      className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                        artworkCollapsed
                          ? "translate-x-5 opacity-0"
                          : "translate-x-0 opacity-100"
                      }`}
                    >
                      {rightPane2PaneInner}
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}
          <ResizeHintTooltip
            alreadySeen={Boolean(uiPrefs.resizeHintSeen)}
            onShown={() => setUiPref("resizeHintSeen", true)}
            resizableActive={useResizableLayout}
          />
        </div>
      )}
      <PasswordProtectedPdfDialog
        fileName={protectedPdfFileName}
        onClose={() => setProtectedPdfFileName(null)}
      />
    </AskPunttDropZone>
  );
}
